/*
 * Real "Buy Now" bottom-sheet checkout - opened by assets/braclo-checkout.js's
 * swapProductBuyNow() in place of Fastrr's hidden .sr-headless-checkout
 * widget. Self-contained: injects its own <style> and DOM into the page at
 * runtime (no Liquid templating), so a single global <script> include works
 * on every product page with no product-specific markup needed. Wired to
 * the exact same backend (routes/checkout.js via /api/checkout/*) and the
 * same Cashfree Element SDK pattern as sections/checkout-v2-main.liquid +
 * assets/braclo-checkout-v2-page.js. Never touches .add-cart-btn or its
 * cart-cycle-btn animation - this only replaces Fastrr's separate
 * per-product Buy Now widget.
 */
(function(){
  if (window.BracloBuyNowSheet) return;

  function apiBase(){ return window.BRACLO_CHAT_API_BASE || ''; }

  var cashfreeSdkPromise = null;
  function loadCashfreeSdk(){
    if (window.Cashfree) return Promise.resolve();
    if (cashfreeSdkPromise) return cashfreeSdkPromise;
    cashfreeSdkPromise = new Promise(function(resolve, reject){
      var existing = document.querySelector('script[src*="sdk.cashfree.com"]');
      if (existing) {
        if (window.Cashfree) { resolve(); return; }
        existing.addEventListener('load', function(){ resolve(); });
        existing.addEventListener('error', function(){ reject(new Error('Cashfree SDK failed to load.')); });
        return;
      }
      var s = document.createElement('script');
      s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      s.onload = function(){ resolve(); };
      s.onerror = function(){ reject(new Error('Cashfree SDK failed to load.')); };
      document.head.appendChild(s);
    });
    return cashfreeSdkPromise;
  }

  function apiFetch(path, opts){
    opts = opts || {};
    var headers = Object.assign({ 'Content-Type': 'application/json' }, opts.headers || {});
    return fetch(apiBase() + '/api/checkout' + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function(res){
      return res.json().then(function(data){
        if (!res.ok) throw new Error(data.error || 'Something went wrong.');
        return data;
      });
    });
  }

  function rawApiFetch(fullPath){
    return fetch(apiBase() + fullPath).then(function(res){
      return res.json().then(function(data){
        if (!res.ok) throw new Error(data.error || 'Something went wrong.');
        return data;
      });
    });
  }

  function formatMoney(cents){
    var n = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
    } catch (e) {
      return '₹' + Math.round(n);
    }
  }

  // Success chime (Paytm/GPay-style "payment confirmed" tone) - synthesized
  // with Web Audio instead of an audio file, so there's no asset to host or
  // fetch. Most mobile browsers only allow audio after a real user gesture,
  // so the AudioContext is created/resumed the moment the customer actually
  // taps to pay (see placeCodOrder/payWithComponent below) - by the time
  // the success screen appears seconds later, it's already unlocked.
  var successAudioCtx = null;
  function ensureAudioUnlocked(){
    try {
      if (!successAudioCtx) successAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (successAudioCtx.state === 'suspended') successAudioCtx.resume();
    } catch (e) {}
  }
  function playSuccessChime(){
    try {
      if (!successAudioCtx) successAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      var ctx = successAudioCtx;
      if (ctx.state === 'suspended') ctx.resume();
      var now = ctx.currentTime;
      // A soft ascending major triad (C5-E5-G5), each note with a gentle
      // attack/decay instead of a flat beep - reads as "pleasant chime"
      // rather than an alert/error tone.
      [523.25, 659.25, 783.99].forEach(function(freq, i){
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        var start = now + i * 0.13;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.22, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.6);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.65);
      });
    } catch (e) {}
  }

  var CSS = ""
  + ".bnws-overlay{position:fixed;inset:0;z-index:99998;background:rgba(10,8,6,.5);opacity:0;pointer-events:none;transition:opacity .28s ease;font-family:'Poppins',sans-serif;}"
  + ".bnws-overlay.is-open{opacity:1;pointer-events:auto;}"
  + ".bnws-sheet{position:fixed;left:0;right:0;bottom:0;z-index:99999;background:#f7f4ef;border-radius:20px 20px 0 0;max-height:92dvh;display:flex;flex-direction:column;transform:translateY(100%);transition:transform .38s cubic-bezier(.19,1,.22,1);box-shadow:0 -20px 50px -20px rgba(0,0,0,.35);}"
  + ".bnws-overlay.is-open .bnws-sheet{transform:translateY(0);}"
  + "@media(min-width:640px){.bnws-sheet{left:50%;right:auto;width:440px;margin-left:-220px;border-radius:20px;bottom:5dvh;max-height:88dvh;}}"
  + ".bnws-sheet *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}"
  + ".bnws-grip{flex:none;display:flex;justify-content:center;padding:9px 0 2px;}"
  + ".bnws-grip i{width:36px;height:4px;border-radius:3px;background:#e2ddd5;display:block;}"
  + ".bnws-header{flex:none;display:flex;align-items:center;gap:10px;padding:6px 18px 12px;}"
  + ".bnws-back{width:32px;height:32px;border-radius:50%;border:none;background:#ece9e2;color:#111010;display:flex;align-items:center;justify-content:center;cursor:pointer;flex:none;transition:opacity .15s ease;}"
  + ".bnws-back[disabled]{opacity:0;pointer-events:none;}"
  + ".bnws-close{width:32px;height:32px;border-radius:50%;border:none;background:#ece9e2;color:#111010;display:flex;align-items:center;justify-content:center;cursor:pointer;flex:none;}"
  + ".bnws-title{font-family:'Jost',sans-serif;font-weight:700;font-size:.9rem;flex:1;display:flex;align-items:center;gap:6px;color:#111010;}"
  + ".bnws-title svg{width:14px;height:14px;flex:none;}"
  + ".bnws-dots{display:flex;align-items:center;gap:6px;flex:none;}"
  + ".bnws-dot{width:7px;height:7px;border-radius:50%;background:#e2ddd5;transition:background .25s ease,width .25s ease;}"
  + ".bnws-dot.is-active{background:#111010;width:16px;border-radius:4px;}"
  + ".bnws-dot.is-done{background:#5a8a6a;}"
  + ".bnws-body{flex:1;overflow-y:auto;padding:0 18px 16px;position:relative;-webkit-overflow-scrolling:touch;}"
  + ".bnws-mini{display:flex;align-items:center;gap:12px;background:#fff;border:1px solid #e2ddd5;border-radius:16px;padding:10px 12px;margin-bottom:16px;}"
  + ".bnws-mini-thumb{width:44px;height:54px;border-radius:9px;flex:none;object-fit:cover;background:#ece9e2;}"
  + ".bnws-mini-info{flex:1;min-width:0;}"
  + ".bnws-mini-name{font-size:.8rem;font-weight:600;line-height:1.3;color:#111010;}"
  + ".bnws-mini-sub{font-size:.7rem;color:#8a7f74;margin-top:1px;}"
  + ".bnws-mini-qty{display:flex;align-items:center;gap:0;border:1.5px solid #e2ddd5;border-radius:999px;padding:3px;flex:none;}"
  + ".bnws-mini-qty button{width:24px;height:24px;border-radius:50%;border:none;background:#ece9e2;color:#111010;font-size:.9rem;font-weight:700;cursor:pointer;}"
  + ".bnws-mini-qty span{width:22px;text-align:center;font-size:.78rem;font-weight:700;}"
  + ".bnws-mini-price{font-family:'Jost',sans-serif;font-weight:800;font-size:.86rem;flex:none;color:#111010;}"
  + ".bnws-panel{display:none;animation:bnwsFade .3s cubic-bezier(.22,1,.36,1) both;}"
  + ".bnws-sheet[data-step='phone'] .bnws-panel-phone{display:block;}"
  + ".bnws-sheet[data-step='loading'] .bnws-panel-loading{display:block;}"
  + ".bnws-sheet[data-step='address'] .bnws-panel-address{display:block;}"
  + ".bnws-sheet[data-step='address-new'] .bnws-panel-address-new{display:block;}"
  + ".bnws-sheet[data-step='payment'] .bnws-panel-payment{display:block;}"
  + "@keyframes bnwsFade{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}"
  + ".bnws-h2{font-family:'Jost',sans-serif;font-size:1rem;font-weight:700;margin:2px 0 14px;color:#111010;letter-spacing:-.01em;}"
  + ".bnws-field-row{display:flex;gap:10px;}"
  + ".bnws-cc{display:flex;align-items:center;gap:5px;padding:0 12px;border:1.5px solid #e2ddd5;border-radius:12px;font-weight:600;font-size:.9rem;background:#fff;flex:none;color:#111010;}"
  + ".bnws-input{width:100%;padding:15px 13px;border:1.5px solid #e2ddd5;border-radius:12px;font-size:16px;font-family:'Poppins',sans-serif;background:#fff;color:#111010;transition:border-color .18s ease;}"
  + ".bnws-input:focus{outline:none;border-color:#111010;}"
  + ".bnws-input::placeholder{color:#b7ab9d;}"
  + ".bnws-trust{display:flex;align-items:center;gap:6px;font-size:.72rem;color:#8a7f74;margin:12px 0 0;}"
  + ".bnws-trust svg{width:13px;height:13px;flex:none;}"
  + ".bnws-btn{width:100%;border:none;border-radius:14px;background:#111010;color:#fff;font-family:'Jost',sans-serif;font-weight:700;font-size:.88rem;min-height:50px;display:flex;align-items:center;justify-content:center;gap:8px;cursor:pointer;margin-top:14px;transition:transform .16s ease,background .16s ease,opacity .16s ease;}"
  + ".bnws-btn:hover:not(:disabled){background:#2a2828;}"
  + ".bnws-btn:active:not(:disabled){transform:scale(.97);}"
  + ".bnws-btn:disabled{opacity:.35;cursor:not-allowed;}"
  + ".bnws-error{font-size:.78rem;color:#c84b3a;margin-top:8px;display:none;}"
  + ".bnws-error.is-visible{display:block;}"
  + ".bnws-resend-row{font-size:.78rem;color:#8a7f74;text-align:center;margin:10px 0 4px;}"
  + ".bnws-resend-link{background:none;border:none;padding:0;margin:0;color:#111010;font-weight:700;text-decoration:underline;cursor:pointer;font-size:inherit;font-family:inherit;}"
  + ".bnws-resend-link:disabled{opacity:.5;cursor:not-allowed;text-decoration:none;}"
  + ".bnws-loading-wrap{display:flex;flex-direction:column;align-items:center;padding:46px 10px 10px;text-align:center;}"
  + ".bnws-spin{width:32px;height:32px;border-radius:50%;border:3px solid #e2ddd5;border-top-color:#c8a97e;animation:bnwsSpin .8s linear infinite;margin-bottom:16px;}"
  + "@keyframes bnwsSpin{to{transform:rotate(360deg);}}"
  + ".bnws-loading-text{font-family:'Jost',sans-serif;font-weight:700;font-size:.9rem;color:#111010;}"
  + ".bnws-addr-card{position:relative;border:1.5px solid #e2ddd5;border-radius:14px;padding:13px 42px 13px 15px;margin-bottom:10px;cursor:pointer;background:#fff;transition:border-color .18s ease,box-shadow .18s ease;}"
  + ".bnws-addr-card.is-selected{border-color:#111010;box-shadow:0 0 0 1.5px #111010;}"
  + ".bnws-addr-tag{display:inline-block;font-size:.62rem;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#a3835f;margin-bottom:4px;}"
  + ".bnws-addr-name{font-weight:600;font-size:.84rem;color:#111010;margin-bottom:2px;}"
  + ".bnws-addr-body{font-size:.76rem;color:#8a7f74;line-height:1.4;}"
  + ".bnws-addr-radio{position:absolute;top:15px;right:15px;width:19px;height:19px;border-radius:50%;border:1.5px solid #e2ddd5;display:flex;align-items:center;justify-content:center;}"
  + ".bnws-addr-card.is-selected .bnws-addr-radio{border-color:#111010;background:#111010;}"
  + ".bnws-addr-radio svg{width:10px;height:10px;color:#fff;opacity:0;transform:scale(.4);transition:opacity .18s ease,transform .18s cubic-bezier(.34,1.56,.64,1);}"
  + ".bnws-addr-card.is-selected .bnws-addr-radio svg{opacity:1;transform:scale(1);}"
  + ".bnws-add-new{width:100%;display:flex;align-items:center;justify-content:center;gap:7px;border:1.5px dashed #e2ddd5;border-radius:14px;background:none;color:#111010;font-family:'Poppins',sans-serif;font-weight:600;font-size:.8rem;padding:13px;cursor:pointer;margin-top:4px;}"
  + ".bnws-add-new svg{width:14px;height:14px;}"
  + ".bnws-progress-note{display:flex;align-items:center;gap:8px;font-size:.74rem;color:#5a8a6a;background:#edf2ee;padding:9px 12px;border-radius:12px;margin-bottom:14px;}"
  + ".bnws-progress-note svg{width:13px;height:13px;flex:none;}"
  + ".bnws-reveal{max-height:0;opacity:0;overflow:hidden;transition:max-height .4s cubic-bezier(.22,1,.36,1),opacity .3s ease,margin .3s ease;}"
  + ".bnws-reveal.is-shown{max-height:200px;opacity:1;margin-bottom:8px;}"
  + ".bnws-lookup-note{font-size:.7rem;color:#5a8a6a;display:flex;align-items:center;gap:6px;margin:6px 2px 4px;min-height:15px;}"
  + ".bnws-lookup-note.is-err{color:#c84b3a;}"
  + ".bnws-lookup-note svg{width:12px;height:12px;}"
  + ".bnws-row2{display:grid;grid-template-columns:1fr 1fr;gap:10px;}"
  + ".bnws-pm{border:1.5px solid #e2ddd5;border-radius:14px;padding:13px 15px;margin-bottom:10px;background:#fff;cursor:pointer;transition:border-color .18s ease,box-shadow .18s ease,background .18s ease;}"
  + ".bnws-pm.is-selected{border-color:#111010;background:#faf8f3;box-shadow:0 0 0 1.5px #111010;}"
  + ".bnws-pm.is-disabled{opacity:.4;pointer-events:none;}"
  + ".bnws-pm-head{display:flex;align-items:center;gap:11px;}"
  + ".bnws-pm-icon{width:36px;height:36px;border-radius:10px;background:#ece9e2;flex:none;display:flex;align-items:center;justify-content:center;font-size:17px;}"
  + ".bnws-pm.is-selected .bnws-pm-icon{background:#fff;}"
  + ".bnws-pm-info{flex:1;min-width:0;}"
  + ".bnws-pm-label{font-weight:700;font-size:.84rem;font-family:'Jost',sans-serif;color:#111010;}"
  + ".bnws-pm-sub{font-size:.7rem;color:#8a7f74;margin-top:1px;}"
  + ".bnws-pm-radio{width:19px;height:19px;border-radius:50%;border:1.5px solid #e2ddd5;flex:none;position:relative;}"
  + ".bnws-pm.is-selected .bnws-pm-radio{border-color:#111010;}"
  + ".bnws-pm.is-selected .bnws-pm-radio::after{content:'';position:absolute;inset:4px;border-radius:50%;background:#111010;}"
  + ".bnws-pm-panel{max-height:0;opacity:0;overflow:hidden;transition:max-height .32s ease,opacity .26s ease,margin .3s ease,padding .3s ease;}"
  + ".bnws-pm.is-selected .bnws-pm-panel.is-open{max-height:600px;opacity:1;margin-top:12px;padding-top:12px;border-top:1px solid #e2ddd5;}"
  + ".bnws-pm-price{font-size:.78rem;font-weight:700;color:#111010;white-space:nowrap;flex:none;}"
  + ".bnws-upi-icon-flash{display:inline-block;animation:bnwsFlashPulse 2.2s ease-in-out infinite;}"
  + "@keyframes bnwsFlashPulse{0%,100%{transform:scale(1) rotate(0deg);filter:drop-shadow(0 0 0 rgba(217,164,6,0));}50%{transform:scale(1.18) rotate(-8deg);filter:drop-shadow(0 0 5px rgba(217,164,6,.6));}}"
  + ".bnws-coupon-card{background:#fff;border:1.5px solid #e2ddd5;border-radius:12px;padding:8px 10px;margin-bottom:10px;}"
  + ".bnws-coupon-row{display:flex;align-items:center;gap:8px;}"
  + ".bnws-coupon-icon{width:24px;height:24px;border-radius:7px;background:#e8f3ea;color:#3f7a4d;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:.7rem;flex:none;}"
  + ".bnws-coupon-row input{flex:1 1 auto;min-width:0;padding:6px 2px;border:none;border-right:1px solid #e2ddd5;border-radius:0;font-size:16px;font-family:'Poppins',sans-serif;background:none;color:#111010;}"
  + ".bnws-coupon-row input:focus{outline:none;}"
  + ".bnws-coupon-row input::placeholder{font-size:.82rem;color:#a09484;}"
  + ".bnws-coupon-apply{background:none;border:none;color:#a3835f;font-weight:700;font-size:.78rem;padding:0 2px;cursor:pointer;flex:none;white-space:nowrap;}"
  + ".bnws-coupon-apply:disabled{opacity:.5;cursor:not-allowed;}"
  + ".bnws-coupon-result{font-size:.68rem;margin-top:6px;display:none;}"
  + ".bnws-coupon-result.is-ok{display:block;color:#5a8a6a;font-weight:600;}"
  + ".bnws-coupon-result.is-bad{display:block;color:#c84b3a;}"
  + ".bnws-coins-card{background:#fff;border:1.5px solid #e2ddd5;border-radius:12px;padding:9px 10px;margin-bottom:10px;}"
  + ".bnws-coins-row{display:flex;align-items:center;gap:10px;}"
  + ".bnws-coins-icon{width:28px;height:28px;border-radius:8px;background:#ece9e2;display:flex;align-items:center;justify-content:center;flex:none;}"
  + ".bnws-coins-icon-coin{width:19px;height:19px;perspective:200px;border-radius:50%;background:radial-gradient(circle at 30% 26%,#fceca0 0%,#e7bd45 40%,#b98a25 75%,#8c661a 100%);box-shadow:0 0 0 1px #a8842f;display:flex;align-items:center;justify-content:center;animation:bnwsCoinSpin 7s linear infinite;}"
  + ".bnws-coins-icon-coin svg{width:10px;height:7.5px;}"
  + "@keyframes bnwsCoinSpin{from{transform:rotateY(0deg);}to{transform:rotateY(360deg);}}"
  + ".bnws-coins-info{flex:1;min-width:0;}"
  + ".bnws-coins-title{font-size:.82rem;font-weight:700;font-family:'Jost',sans-serif;color:#111010;}"
  + ".bnws-coins-sub{font-size:.7rem;color:#8a7f74;margin-top:1px;}"
  + ".bnws-coins-redeem{background:none;border:1.5px solid #e2ddd5;border-radius:20px;padding:7px 14px;color:#111010;font-weight:700;font-size:.76rem;cursor:pointer;flex:none;}"
  + ".bnws-coins-redeem:disabled{opacity:.5;cursor:not-allowed;}"
  + ".bnws-coins-applied{display:none;align-items:center;justify-content:space-between;gap:12px;margin-top:9px;padding-top:9px;border-top:1px solid #e3d3b8;font-size:.8rem;color:#5a8a6a;font-weight:600;}"
  + ".bnws-coins-applied.is-shown{display:flex;}"
  + ".bnws-coins-applied svg{width:13px;height:13px;vertical-align:-2px;margin-right:4px;}"
  + ".bnws-coins-remove{background:none;border:none;color:#8a7f74;font-weight:700;font-size:.74rem;cursor:pointer;text-decoration:underline;flex:none;}"
  + ".bnws-modal-overlay{position:fixed;inset:0;background:rgba(17,16,16,.5);z-index:100000;display:flex;align-items:flex-end;justify-content:center;opacity:0;pointer-events:none;transition:opacity .25s ease;}"
  + ".bnws-modal-overlay.is-open{opacity:1;pointer-events:auto;}"
  + ".bnws-modal-sheet{background:#f7f4ef;width:100%;max-width:440px;border-radius:20px 20px 0 0;max-height:82dvh;display:flex;flex-direction:column;transform:translateY(100%);transition:transform .3s cubic-bezier(.22,1,.36,1);}"
  + ".bnws-modal-overlay.is-open .bnws-modal-sheet{transform:translateY(0);}"
  + ".bnws-modal-header{display:flex;align-items:center;justify-content:space-between;padding:16px 18px;border-bottom:1px solid #e2ddd5;font-family:'Jost',sans-serif;font-weight:700;font-size:.94rem;flex:none;color:#111010;}"
  + ".bnws-modal-close{background:#ece9e2;border:none;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:1.1rem;line-height:1;color:#111010;cursor:pointer;flex:none;}"
  + ".bnws-modal-body{padding:14px 18px 20px;overflow-y:auto;-webkit-overflow-scrolling:touch;}"
  + ".bnws-modal-amount-row{display:flex;justify-content:space-between;align-items:center;font-size:.84rem;font-weight:700;margin-bottom:12px;color:#111010;}"
  + ".bnws-nb-search input{width:100%;padding:11px 13px;border:1.5px solid #e2ddd5;border-radius:10px;font-size:16px;font-family:'Poppins',sans-serif;background:#fff;color:#111010;}"
  + ".bnws-nb-search input:focus{outline:none;border-color:#c98f18;}"
  + ".bnws-nb-filter-chips{display:flex;gap:8px;margin:11px 0 4px;overflow-x:auto;}"
  + ".bnws-chip{padding:6px 13px;border-radius:999px;border:1.5px solid #e2ddd5;background:#fff;font-size:.72rem;font-weight:600;color:#8a7f74;cursor:pointer;white-space:nowrap;flex:none;}"
  + ".bnws-chip.is-active{border-color:#111010;color:#111010;background:#f7f4ef;}"
  + ".bnws-nb-list{display:flex;flex-direction:column;margin-top:6px;}"
  + ".bnws-nb-row{position:relative;display:flex;align-items:center;gap:11px;padding:12px 2px;border-bottom:1px solid #e2ddd5;cursor:pointer;-webkit-tap-highlight-color:transparent;}"
  + ".bnws-nb-row:last-child{border-bottom:none;}"
  + ".bnws-nb-row.is-selected{background:#f7f4ef;}"
  + ".bnws-nb-row.is-mounting{opacity:.6;}"
  + ".bnws-nb-row.is-unavailable{opacity:.4;pointer-events:none;}"
  + ".bnws-nb-row-mount{position:absolute;inset:0;pointer-events:none;}"
  + ".bnws-nb-row-mount iframe{width:100%;height:100%;border:none;display:block;opacity:0;}"
  + ".bnws-nb-row-logo{width:32px;height:32px;border-radius:9px;flex:none;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:.86rem;position:relative;z-index:1;}"
  + ".bnws-nb-row-name{flex:1;font-size:.84rem;font-weight:600;color:#111010;position:relative;z-index:1;}"
  + ".bnws-nb-row-arrow{color:#8a7f74;font-size:1.15rem;flex:none;position:relative;z-index:1;}"
  + ".bnws-upi-apps{display:flex;gap:16px;padding:2px 0 6px;}"
  + ".bnws-upi-app{position:relative;flex:0 0 auto;width:48px;padding:0;border:none;background:none;cursor:pointer;text-align:center;}"
  + ".bnws-upi-app.is-unavailable{opacity:.35;pointer-events:none;}"
  + ".bnws-upi-app-mount{position:absolute;inset:0;border-radius:50%;overflow:hidden;pointer-events:none;}"
  + ".bnws-upi-app-mount iframe{width:100%;height:100%;border:none;display:block;opacity:0;}"
  + ".bnws-upi-app-logo{display:block;width:38px;height:38px;margin:0 auto 5px;border-radius:50%;overflow:hidden;position:relative;z-index:1;pointer-events:none;}"
  + ".bnws-upi-app.is-selected .bnws-upi-app-logo{box-shadow:0 0 0 2px #111010;}"
  + ".bnws-upi-app-logo svg{width:100%;height:100%;display:block;}"
  + ".bnws-upi-app-name{display:block;font-size:.6rem;font-weight:500;color:#8a7f74;position:relative;z-index:1;pointer-events:none;}"
  + ".bnws-field-label{display:block;font-size:.68rem;letter-spacing:.03em;text-transform:uppercase;color:#8a7f74;margin-bottom:5px;}"
  + ".bnws-cf-mount{position:relative;border:1.5px solid #e2ddd5;border-radius:12px;padding:0 12px;background:#fff;min-height:48px;display:flex;align-items:center;margin-bottom:10px;}"
  + ".bnws-cf-mount iframe{width:100%;border:none;display:block;}"
  + ".bnws-processing{position:fixed;inset:0;z-index:100002;background:#f7f4ef;display:none;flex-direction:column;align-items:center;justify-content:center;padding:30px;}"
  + ".bnws-processing.is-shown{display:flex;}"
  + ".bnws-proc-ring{position:relative;width:76px;height:76px;margin-bottom:24px;}"
  + ".bnws-proc-ring svg{width:100%;height:100%;transform:rotate(-90deg);}"
  + ".bnws-proc-ring circle{fill:none;stroke-width:5;}"
  + ".bnws-proc-track{stroke:#e2ddd5;}"
  + ".bnws-proc-fill{stroke:#111010;stroke-linecap:round;stroke-dasharray:201;stroke-dashoffset:201;transition:stroke-dashoffset .5s cubic-bezier(.22,1,.36,1);}"
  + ".bnws-proc-icon{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;}"
  + ".bnws-proc-icon svg{width:22px;height:22px;color:#111010;}"
  + ".bnws-proc-status{display:flex;flex-direction:column;gap:11px;width:100%;max-width:220px;}"
  + ".bnws-proc-item{display:flex;align-items:center;gap:9px;font-size:.8rem;color:#8a7f74;font-weight:500;opacity:.4;transition:opacity .3s ease,color .3s ease;}"
  + ".bnws-proc-item.is-active{opacity:1;color:#111010;font-weight:600;}"
  + ".bnws-proc-item.is-done{opacity:1;color:#5a8a6a;font-weight:600;}"
  + ".bnws-proc-dot{width:17px;height:17px;border-radius:50%;border:2px solid currentColor;flex:none;position:relative;display:flex;align-items:center;justify-content:center;}"
  + ".bnws-proc-item.is-done .bnws-proc-dot{background:#5a8a6a;border-color:#5a8a6a;}"
  + ".bnws-proc-dot svg{width:9px;height:9px;color:#fff;opacity:0;transform:scale(.5);transition:opacity .2s ease,transform .2s cubic-bezier(.34,1.56,.64,1);}"
  + ".bnws-proc-item.is-done .bnws-proc-dot svg{opacity:1;transform:scale(1);}"
  + ".bnws-success{position:fixed;inset:0;z-index:100002;background:#f7f4ef;display:none;flex-direction:column;}"
  + ".bnws-success.is-shown{display:flex;}"
  + ".bnws-success-scroll{flex:1;overflow-y:auto;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:50px 24px 20px;text-align:center;width:100%;max-width:440px;margin:0 auto;}"
  + ".bnws-check-wrap{width:70px;height:70px;border-radius:50%;background:#edf2ee;display:flex;align-items:center;justify-content:center;margin-bottom:20px;}"
  + ".bnws-check-wrap svg{width:34px;height:34px;}"
  + ".bnws-check-circle{fill:none;stroke:#5a8a6a;stroke-width:2.4;stroke-dasharray:76;stroke-dashoffset:76;animation:bnwsDrawCircle .5s ease forwards;}"
  + ".bnws-check-tick{fill:none;stroke:#5a8a6a;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:24;stroke-dashoffset:24;animation:bnwsDrawTick .35s ease .45s forwards;}"
  + "@keyframes bnwsDrawCircle{to{stroke-dashoffset:0;}}"
  + "@keyframes bnwsDrawTick{to{stroke-dashoffset:0;}}"
  + ".bnws-success-title{font-family:'Jost',sans-serif;font-size:1.2rem;font-weight:800;margin:0 0 6px;color:#111010;letter-spacing:-.01em;}"
  + ".bnws-success-sub{font-size:.82rem;color:#8a7f74;margin:0 0 22px;}"
  + ".bnws-success-card{width:100%;background:#fff;border:1px solid #e2ddd5;border-radius:16px;padding:14px 16px;text-align:left;margin-bottom:14px;}"
  + ".bnws-success-row{display:flex;align-items:center;justify-content:space-between;padding:8px 0;}"
  + ".bnws-success-row+.bnws-success-row{border-top:1px solid #e2ddd5;}"
  + ".bnws-success-label{font-size:.7rem;color:#8a7f74;font-weight:500;}"
  + ".bnws-success-value{font-size:.82rem;font-weight:700;font-family:'Jost',sans-serif;color:#111010;}"
  + ".bnws-success-footer{flex:none;padding:12px 20px calc(12px + env(safe-area-inset-bottom));display:flex;flex-direction:column;gap:8px;width:100%;max-width:440px;margin:0 auto;}"
  + ".bnws-btn-ghost{width:100%;border:1.5px solid #e2ddd5;border-radius:14px;background:none;color:#111010;font-family:'Jost',sans-serif;font-weight:700;font-size:.84rem;min-height:46px;display:flex;align-items:center;justify-content:center;gap:7px;cursor:pointer;}"
  + ".bnws-welcome-back{display:flex;align-items:center;gap:10px;background:#edf2ee;border-radius:12px;padding:10px 12px;margin-bottom:14px;}"
  + ".bnws-welcome-emoji{font-size:1.05rem;flex:none;}"
  + ".bnws-welcome-title{font-size:.82rem;font-weight:700;color:#111010;font-family:'Jost',sans-serif;}"
  + ".bnws-welcome-title span{font-weight:700;}"
  + ".bnws-welcome-sub{font-size:.7rem;color:#5a8a6a;font-weight:600;margin-top:1px;}"
  + ".bnws-delivery-card{background:#fff;border:1.5px solid #e2ddd5;border-radius:14px;padding:12px 14px 14px;margin-top:4px;margin-bottom:12px;}"
  + ".bnws-delivery-row{display:flex;align-items:center;gap:10px;margin-bottom:14px;}"
  + ".bnws-delivery-icon{font-size:1.15rem;flex:none;}"
  + ".bnws-delivery-label{font-size:.64rem;letter-spacing:.03em;text-transform:uppercase;color:#8a7f74;font-weight:600;}"
  + ".bnws-delivery-date{font-size:.86rem;font-weight:700;color:#111010;font-family:'Jost',sans-serif;margin-top:1px;}"
  + ".bnws-delivery-track{display:flex;align-items:flex-start;justify-content:space-between;position:relative;padding:0 4px;}"
  + ".bnws-delivery-track::before{content:'';position:absolute;left:14px;right:14px;top:5px;height:2px;background:#e2ddd5;z-index:0;}"
  + ".bnws-delivery-step{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;gap:6px;font-size:.6rem;color:#b7ab9d;font-weight:600;flex:1;}"
  + ".bnws-delivery-dot{width:11px;height:11px;border-radius:50%;background:#e2ddd5;border:2px solid #fff;}"
  + ".bnws-delivery-step.is-active{color:#111010;}"
  + ".bnws-delivery-step.is-active .bnws-delivery-dot{background:#c98f18;animation:bnwsDeliveryPulse 1.8s ease-in-out infinite;}"
  + "@keyframes bnwsDeliveryPulse{0%,100%{box-shadow:0 0 0 0 rgba(201,143,24,.45);}50%{box-shadow:0 0 0 5px rgba(201,143,24,0);}}"
  + ".bnws-recovery-card{display:none;background:#fdf3ee;border:1.5px solid #f0dbc9;border-radius:14px;padding:14px 16px;margin-top:10px;}"
  + ".bnws-recovery-card.is-visible{display:block;}"
  + ".bnws-recovery-title{font-size:.86rem;font-weight:700;color:#111010;margin:0 0 2px;font-family:'Jost',sans-serif;}"
  + ".bnws-recovery-sub{font-size:.74rem;color:#8a7f74;margin:0 0 12px;}"
  + ".bnws-recovery-actions{display:flex;flex-direction:column;gap:8px;}"
  + ".bnws-recovery-btn{width:100%;border:none;border-radius:10px;background:#111010;color:#fff;font-family:'Jost',sans-serif;font-weight:700;font-size:.8rem;padding:11px;cursor:pointer;}"
  + ".bnws-recovery-btn-ghost{width:100%;border:1.5px solid #e2ddd5;border-radius:10px;background:none;color:#111010;font-family:'Jost',sans-serif;font-weight:700;font-size:.8rem;padding:11px;cursor:pointer;}"
  + ".bnws-saved-toast{position:fixed;left:50%;bottom:24px;transform:translate(-50%,12px);background:#111010;color:#fff;font-family:'Poppins',sans-serif;font-size:.8rem;font-weight:600;padding:12px 18px;border-radius:12px;box-shadow:0 10px 30px -8px rgba(0,0,0,.4);z-index:100001;opacity:0;pointer-events:none;transition:opacity .3s ease,transform .3s ease;max-width:88vw;text-align:center;}"
  + ".bnws-saved-toast.is-shown{opacity:1;transform:translate(-50%,0);}"
  + "@media(prefers-reduced-motion:reduce){.bnws-overlay,.bnws-sheet,.bnws-panel,.bnws-reveal,.bnws-pm-panel,.bnws-coins-icon-coin,.bnws-upi-icon-flash,.bnws-delivery-step.is-active .bnws-delivery-dot{transition:none!important;animation:none!important;}}"
  ;

  function injectStyle(){
    if (document.getElementById('bnws-style')) return;
    var s = document.createElement('style');
    s.id = 'bnws-style';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  var ICONS = {
    shield: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    back: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>',
    upi: '⚡', card: '💳', cod: '📦',
    phonepe: '<svg viewBox="0 0 40 40"><rect width="40" height="40" rx="11" fill="#5F259F"/><svg x="8" y="8" width="24" height="24" viewBox="0 0 24 24"><path fill="#fff" d="M10.206 9.941h2.949v4.692c-.402.201-.938.268-1.34.268-1.072 0-1.609-.536-1.609-1.743V9.941zm13.47 4.816c-1.523 6.449-7.985 10.442-14.433 8.919C2.794 22.154-1.199 15.691.324 9.243 1.847 2.794 8.309-1.199 14.757.324c6.449 1.523 10.442 7.985 8.919 14.433zm-6.231-5.888a.887.887 0 0 0-.871-.871h-1.609l-3.686-4.222c-.335-.402-.871-.536-1.407-.402l-1.274.401c-.201.067-.268.335-.134.469l4.021 3.82H6.386c-.201 0-.335.134-.335.335v.67c0 .469.402.871.871.871h.938v3.217c0 2.413 1.273 3.82 3.418 3.82.67 0 1.206-.067 1.877-.335v2.145c0 .603.469 1.072 1.072 1.072h.938a.432.432 0 0 0 .402-.402V9.874h1.542c.201 0 .335-.134.335-.335v-.67z"/></svg></svg>',
    gpay: '<svg viewBox="0 0 40 40"><rect width="40" height="40" rx="11" fill="#fff" stroke="#e2ddd5"/><svg x="8" y="8" width="24" height="24" viewBox="0 0 24 24"><path fill="#3C4043" d="M3.963 7.235A3.963 3.963 0 00.422 9.419a3.963 3.963 0 000 3.559 3.963 3.963 0 003.541 2.184c1.07 0 1.97-.352 2.627-.957.748-.69 1.18-1.71 1.18-2.916a4.722 4.722 0 00-.07-.806H3.964v1.526h2.14a1.835 1.835 0 01-.79 1.205c-.356.241-.814.379-1.35.379-1.034 0-1.911-.697-2.225-1.636a2.375 2.375 0 010-1.517c.314-.94 1.191-1.636 2.225-1.636a2.152 2.152 0 011.52.594l1.132-1.13a3.808 3.808 0 00-2.652-1.033zm6.501.55v6.9h.886V11.89h1.465c.603 0 1.11-.196 1.522-.588a1.911 1.911 0 00.635-1.464 1.92 1.92 0 00-.635-1.456 2.125 2.125 0 00-1.522-.598zm2.427.85a1.156 1.156 0 01.823.365 1.176 1.176 0 010 1.686 1.171 1.171 0 01-.877.357H11.35V8.635h1.487a1.156 1.156 0 01.054 0zm4.124 1.175c-.842 0-1.477.308-1.907.925l.781.491c.288-.417.68-.626 1.175-.626a1.255 1.255 0 01.856.323 1.009 1.009 0 01.366.785v.202c-.34-.193-.774-.289-1.3-.289-.617 0-1.11.145-1.479.434-.37.288-.554.677-.554 1.165a1.476 1.476 0 00.525 1.156c.35.308.785.463 1.305.463.61 0 1.098-.27 1.465-.81h.038v.655h.848v-2.909c0-.61-.19-1.09-.568-1.44-.38-.35-.896-.525-1.551-.525zm2.263.154l1.946 4.422-1.098 2.38h.915L24 9.963h-.965l-1.368 3.391h-.02l-1.406-3.39zm-2.146 2.368c.494 0 .88.11 1.156.33 0 .372-.147.696-.44.973a1.413 1.413 0 01-.997.414 1.081 1.081 0 01-.69-.232.708.708 0 01-.293-.578c0-.257.12-.47.363-.647.24-.173.54-.26.9-.26Z"/></svg></svg>',
    paytm: '<svg viewBox="0 0 40 40"><rect width="40" height="40" rx="11" fill="#00BAF2"/><svg x="8" y="8" width="24" height="24" viewBox="0 0 24 24"><path fill="#fff" d="M15.85 8.167a.204.204 0 0 0-.04.004c-.68.19-.543 1.148-1.781 1.23h-.12a.23.23 0 0 0-.052.005h-.001a.24.24 0 0 0-.184.235v1.09c0 .134.106.241.237.241h.645v4.623c0 .132.104.238.233.238h1.058a.236.236 0 0 0 .233-.238v-4.623h.6c.13 0 .236-.107.236-.241v-1.09a.239.239 0 0 0-.236-.24h-.612V8.386a.218.218 0 0 0-.216-.22z"/></svg></svg>',
    bcoin: '<svg viewBox="6650 10240 7980 5945" preserveAspectRatio="xMidYMid meet" aria-hidden="true"><path fill="#3a4650" d="M13061.47 13346.9c-1.11,1.33 -2.21,2.62 -3.27,3.93 -12.91,-9.18 -26.26,-18.14 -38.7,-27.96 -63.19,-49.18 -79.59,-104.71 -56.18,-178.84 6.75,33.91 7.63,75.88 24.02,110.18 16.64,34.76 48.78,62.08 74.13,92.69zm-217.51 -605.94c-10.27,7.65 -14.21,23.6 -20.77,36.04 -2.62,0.24 -5.01,0.47 -7.65,0.68 -0.65,-11.83 -6.12,-26.25 -1.32,-34.78 16.61,-28.61 45.68,-15.52 71.49,-13.33 -14.2,3.5 -31.03,3.5 -41.75,11.39zm351.06 -123.32c20.57,-11.56 41.55,-22.29 61.86,-34.1 42.85,-25.14 52.03,-54.87 29.97,-109.71 -3.06,-7.87 -8.11,-15.11 -15.33,-28.2 41.98,20.11 59.05,91.37 31.51,122.39 -15.31,17.71 -37.39,30.39 -58.37,41.77 -20.99,11.59 -44.18,19.03 -66.46,28.18 -1.09,-1.94 -2.4,-3.69 -3.49,-5.46 6.75,-5.03 13.09,-10.71 20.31,-14.87z"/><path fill="#3a4650" d="M8042.73 14590.07c130.58,100.01 252.51,191.48 371.85,286.22 15.84,12.69 27.3,41.24 27.19,62.25 -0.9,114.85 -6.18,229.69 -8.99,344.54 -0.56,17.19 -1.24,37.87 7.19,51.13 77.54,120.8 116.98,270.7 244.08,354.2 107.31,70.57 78.88,170.8 79.89,274.3 -237.55,0 -472.97,0 -722.22,0 57.2,-180.47 -55.29,-302.73 -135.63,-435.78 -56.86,-94.17 -116.2,-186.77 -171.6,-281.95 -10.45,-17.75 -17.3,-53.04 -7.41,-64.95 145.07,-172.94 239.46,-372.74 315.65,-589.96zm5447.86 -2747.86c-2.59,383.53 -1.91,766.5 0,1150.03 0,27.53 10,58.88 25.73,82.03 66.41,100.35 139.12,196.2 205.53,296.44 26.19,39.44 57.09,43.26 95.86,33.15 178.11,-44.28 319.02,-139.57 409.93,-303.3 10.57,-18.65 31.36,-31.8 46.98,-47.53 8.76,23.15 27.53,48.32 23.82,70.01 -22.03,135.63 -85.97,250.37 -188.68,340.38 -62.14,53.94 -131.02,99.78 -202.49,153.05 27.64,38.77 59.44,79.56 88.55,122.71 24.27,36.41 13.03,68.55 -30.9,68.55 -199.92,-2.14 -400.39,0.45 -599.07,-16.97 -68.21,-6.41 -147.88,-50.34 -194.29,-102.82 -127.88,-144.96 -289.47,-244.41 -439.38,-359.26 -108.44,-83.38 -202.27,-181.14 -249.36,-312.62 -13.14,-37.08 -15.05,-77.76 -25.17,-132.38 -21.8,93.5 -8.76,168.11 34.61,234.64 43.83,66.41 89.57,133.95 145.3,190.36 55.18,55.29 123.61,97.2 190.59,148.56 -115.41,25.17 -204.86,9.32 -268.35,-94.51 -50.12,-82.82 -109,-159.91 -173.39,-239.69 12.36,36.52 18.65,77.31 37.98,109.11 55.85,89.23 118.55,173.06 174.85,261.38 14.5,23.26 16.97,54.5 25.29,81.47 -11.35,5.62 -22.7,11.24 -33.83,16.97 -81.02,-101.02 -161.14,-202.5 -241.94,-304.08 -3.26,1.46 -6.29,2.7 -8.87,3.82 28.09,51.47 53.94,103.61 85.29,153.05 22.58,35.62 50.68,67.65 77.65,100.91 94.73,118.33 65.06,232.95 -76.53,297.12 -63.15,28.77 -123.95,64.39 -189.12,88.89 -20.23,7.52 -60.8,-2.03 -72.71,-17.65 -63.38,-80.79 -121.7,-165.41 -178,-251.26 -75.85,-114.74 -69.56,-234.98 -16.97,-357.8 74.06,-172.27 158.56,-342.18 138.56,-538.38 -11.35,-105.97 -59,-186.09 -151.03,-235.76 33.15,62.14 72.59,121.81 97.77,186.2 44.5,112.94 4.94,222.5 -29.45,329.82 -19.55,59.56 -39.55,120.24 -70.23,173.39 -122.71,210.14 -79.11,406.12 63.94,577.37 102.15,121.37 136.08,236.1 70.12,381.51 -21.91,49.56 -23.71,107.88 -43.94,158.67 -74.5,189.35 -25.62,380.95 -13.71,571.53 13.71,217.45 33.94,434.33 56.53,650.42 4.38,39.67 26.85,92.93 57.65,110.35 120.23,68.43 129.56,169.91 104.61,293.41l-1225.31 0c22.02,-133.05 31.35,-260.15 67.2,-379.83 35.62,-120.35 33.15,-232.5 6.18,-352.96 -30.12,-135.86 -46.41,-274.98 -61.92,-414.21 -12.02,-110.24 -45.85,-208.56 -121.03,-292.62 -203.73,-227.55 -327.79,-495.68 -391.73,-791.55 -16.29,-75.07 -2.47,-156.77 -2.47,-230.03 -39.56,67.76 -52.59,152.94 -37.65,240.03 52.03,306.55 186.77,574.11 391.06,807.29 66.41,75.74 104.62,163.61 110.91,244.41 -145.29,-166.76 -307.79,-339.14 -453.65,-524.56 -70.23,-88.44 -109.79,-201.15 -157.88,-292.62 -12.03,81.36 11.12,174.07 72.59,250.48 103.39,128.11 214.19,250.37 324.09,373.08 90.91,101.58 178.56,203.62 177.21,361.62 -130.24,23.71 -259.36,49.44 -389.15,71.91 -285.76,50.24 -569.62,114.74 -857.97,146.09 -420.5,45.74 -820.89,-47.65 -1176.88,-274.42 -270.15,-172.26 -508.83,-388.02 -724.59,-627.94 -43.15,-47.64 -93.27,-89.67 -152.15,-123.05 70.68,76.08 140.92,152.38 213.62,226.32 173.62,175.53 167.44,137.89 79.67,356.68 -75.85,187.32 -178,357.79 -329.7,496.24 -13.82,12.58 -20,44.61 -16.18,65.29 19.21,110.23 41.24,221.15 66.41,330.82 4.83,23.94 21.24,54.5 41.24,65.18 81.47,42.59 102.82,108.55 90.24,192.49 -3.04,23.94 -2.36,48.89 -3.71,77.65l-692.44 0c-4.39,-6.29 -10.12,-10 -9.56,-13.82 16.41,-234.41 -119,-423.64 -184.06,-633.56 -12.03,-37.42 -23.26,-77.2 -23.94,-115.97 -1.35,-505.68 -1.91,-1012.03 -0.56,-1517.82 1.24,-783.47 629.74,-1479.73 1409.38,-1546.71 116.65,-10.11 242.62,31.35 356.57,70.12 406.79,139.8 777.17,343.53 1078.55,658.17 3.26,3.15 9.56,3.6 33.27,11.8 -19.33,-31.35 -27.42,-52.59 -42.03,-68.21 -188.56,-197.55 -405.44,-356.67 -652.44,-473.88 -114.51,-54.61 -231.83,-104.5 -350.94,-176.09 50.34,-10 100.35,-27.75 151.03,-28.2 255.2,-5.62 510.85,-8.2 765.93,-8.77 581.53,-0.56 1162.5,-1.34 1743.47,1.13 88.33,0.67 167.44,-11.69 243.07,-58.77 55.17,-33.83 117.32,-57.09 171.82,-91.48 48.88,-31.35 87.09,-20.11 117.88,19.45 41.91,52.59 86.41,105.85 116.53,165.41 161.03,314.53 221.93,640.53 91.58,983.15 -37.64,99.79 -74.05,199.35 -112.37,303.52 100.35,-74.61 245.2,-561.53 222.61,-772.11 -30.68,-278.46 -119.12,-535.91 -307.23,-751.33 58.43,-55.85 87.88,-54.05 137.32,6.74 178.56,221.83 255.2,475.79 245.09,759.53 -2.48,86.53 10.11,173.73 19.44,260.71 2.47,25.73 17.64,50.23 29.44,82.82 -9.55,-220.7 -1.91,-435 -33.71,-642.44 -21.58,-135.97 -99.79,-263.74 -155.64,-393.64 -15.62,-36.19 -41.92,-67.65 -63.83,-101.48 89.68,-33.26 142.27,-21.91 184.86,66.64 63.37,130.8 111.47,269.36 158.55,385.22 16.97,-149.69 -63.26,-299.59 -139.11,-463.77l134.84 0c16.75,-50.79 26.19,-113.95 40.12,-115.29 40.68,-3.26 83.27,18.87 141.48,35.84 40.79,-53.37 136.53,-85.96 124.17,-209.46 -10.79,-106.64 93.16,-179.24 207.44,-176.76 33.27,0.78 75.29,-10.57 98.33,-31.92 203.62,-188.67 404.88,-380.38 604.12,-573.89 20.67,-20.11 32.59,-61.58 28.88,-90.35 -15.06,-119.67 35.73,-228.79 139.9,-282.73 37.2,-19.1 75.52,-29.44 113.28,-31.12l22.47 0c65.18,3.03 127.66,32.36 179.24,86.97 51.57,54.62 96.07,116.42 150.58,167.21 83.26,78.1 120.12,165.53 123.61,254.42l0 27.53c-3.6,93.6 -42.59,188.22 -101.81,274.64 -18.77,27.53 -60.69,39.55 -89,57.08 -4.95,58.89 -9.22,116.53 -13.83,174.29 -8.09,1.13 -16.96,2.93 -25.06,4.27 -21.8,-44.49 -43.15,-88.99 -70.68,-145.97 -151.03,69.56 -305.77,127.77 -447.47,209.24 -117.88,67.65 -166.65,191.82 -167.32,326.56zm-639.97 -165.3c-2.02,-33.72 -17.75,-75.41 -3.59,-99.68 47.64,-81.92 131.02,-44.39 204.52,-38.21 -40.46,9.78 -88.67,9.56 -119.68,32.48 -29.22,21.8 -40.34,67.76 -59.45,103.16 -7.19,0.79 -14.49,1.57 -21.8,2.25zm695.93 1643.46c-37.2,-26.64 -75.4,-52.15 -111.25,-80.35 -181.37,-141.48 -227.89,-300.49 -160.69,-512.76 19.1,97.2 21.46,217.1 68.32,315.66 47.87,99.9 139.79,178.22 212.5,265.98 -3.04,3.82 -5.96,7.53 -8.88,11.47zm611.2 -2595.6c120.57,57.53 169.34,261.72 90.8,351.16 -44.28,50.35 -107.55,86.64 -167.44,119.68 -60.12,33.04 -126.87,54.17 -190.59,80.57 -3.48,-5.28 -6.96,-10.33 -10.22,-15.62 19.33,-14.27 37.53,-30.67 58.32,-42.59 58.66,-33.48 119.12,-63.6 177.33,-97.76 122.71,-71.92 149.56,-157.21 86.07,-314.65 -9.1,-22.47 -23.37,-43.03 -44.27,-80.79z"/></svg>'
  };

  var els = {};
  function buildDom(){
    if (document.getElementById('bnwsOverlay')) return;
    var wrap = document.createElement('div');
    wrap.innerHTML =
      '<div class="bnws-overlay" id="bnwsOverlay">' +
        '<div class="bnws-sheet" id="bnwsSheet" data-step="phone">' +
          '<div class="bnws-grip"><i></i></div>' +
          '<div class="bnws-header">' +
            '<button type="button" class="bnws-back" id="bnwsBack">' + ICONS.back + '</button>' +
            '<span class="bnws-title">' + ICONS.shield + ' Secure Checkout</span>' +
            '<div class="bnws-dots" id="bnwsDots"><span class="bnws-dot" data-dot="address"></span><span class="bnws-dot" data-dot="payment"></span><span class="bnws-dot" data-dot="done"></span></div>' +
            '<button type="button" class="bnws-close" id="bnwsClose">' + ICONS.close + '</button>' +
          '</div>' +
          '<div class="bnws-body" id="bnwsBody">' +

            '<div class="bnws-panel bnws-panel-phone">' +
              '<h2 class="bnws-h2">Enter your mobile number</h2>' +
              '<div class="bnws-field-row">' +
                '<span class="bnws-cc">🇮🇳 +91</span>' +
                '<input class="bnws-input" id="bnwsPhone" type="tel" inputmode="numeric" maxlength="10" placeholder="98765 43210">' +
              '</div>' +
              '<p class="bnws-error" id="bnwsPhoneError"></p>' +
              '<p class="bnws-trust">' + ICONS.lock + ' OTP will be sent to you on WhatsApp</p>' +
              '<button type="button" class="bnws-btn" id="bnwsSendOtp" disabled>Send OTP</button>' +
            '</div>' +

            '<div class="bnws-panel bnws-panel-otp" style="display:none" id="bnwsOtpPanel">' +
              '<h2 class="bnws-h2">Enter the 6-digit code</h2>' +
              '<input class="bnws-input" id="bnwsOtpCode" type="text" inputmode="numeric" maxlength="6" placeholder="000000">' +
              '<p class="bnws-error" id="bnwsOtpError"></p>' +
              '<p class="bnws-resend-row">Didn\'t get it? <button type="button" class="bnws-resend-link" id="bnwsResendLink">Resend OTP</button></p>' +
              '<button type="button" class="bnws-btn" id="bnwsVerifyOtp" disabled>Verify</button>' +
            '</div>' +

            '<div class="bnws-panel bnws-panel-loading">' +
              '<div class="bnws-loading-wrap"><div class="bnws-spin"></div><p class="bnws-loading-text">Finding your saved details…</p></div>' +
            '</div>' +

            '<div class="bnws-panel bnws-panel-address">' +
              '<div class="bnws-mini" id="bnwsMiniAddr"></div>' +
              '<div class="bnws-welcome-back" id="bnwsWelcomeBack" hidden>' +
                '<span class="bnws-welcome-emoji">👋</span>' +
                '<div><div class="bnws-welcome-title">Welcome back<span id="bnwsWelcomeName"></span></div><div class="bnws-welcome-sub">Your details are ready.</div></div>' +
              '</div>' +
              '<h2 class="bnws-h2">Where should we deliver?</h2>' +
              '<div id="bnwsAddrList"></div>' +
              '<div class="bnws-delivery-card" id="bnwsDeliveryCard" hidden>' +
                '<div class="bnws-delivery-row"><span class="bnws-delivery-icon">📦</span><div><div class="bnws-delivery-label">Estimated delivery</div><div class="bnws-delivery-date" id="bnwsDeliveryDate"></div></div></div>' +
                '<div class="bnws-delivery-track">' +
                  '<span class="bnws-delivery-step is-active" data-step="order"><span class="bnws-delivery-dot"></span>Order</span>' +
                  '<span class="bnws-delivery-step" data-step="packed"><span class="bnws-delivery-dot"></span>Packed</span>' +
                  '<span class="bnws-delivery-step" data-step="shipped"><span class="bnws-delivery-dot"></span>Shipped</span>' +
                  '<span class="bnws-delivery-step" data-step="delivered"><span class="bnws-delivery-dot"></span>Delivered</span>' +
                '</div>' +
              '</div>' +
              '<button type="button" class="bnws-add-new" id="bnwsAddNewBtn">' + ICONS.plus + ' Add New Address</button>' +
            '</div>' +

            '<div class="bnws-panel bnws-panel-address-new">' +
              '<h2 class="bnws-h2">Add a new address</h2>' +
              '<p class="bnws-progress-note">' + ICONS.check + ' Using +91 <span id="bnwsUsingPhone"></span></p>' +
              '<input class="bnws-input" id="bnwsPincode" type="text" inputmode="numeric" maxlength="6" placeholder="Pincode" style="margin-bottom:0;">' +
              '<p class="bnws-lookup-note" id="bnwsLookupNote"></p>' +
              '<div class="bnws-reveal" id="bnwsRevealCityState"><div class="bnws-row2"><input class="bnws-input" id="bnwsCity" type="text" placeholder="City"><input class="bnws-input" id="bnwsState" type="text" placeholder="State"></div></div>' +
              '<div class="bnws-reveal" id="bnwsRevealAddr1"><input class="bnws-input" id="bnwsAddress1" type="text" placeholder="House / Flat / Street / Area" style="margin-bottom:10px;"></div>' +
              '<div class="bnws-reveal" id="bnwsRevealName"><input class="bnws-input" id="bnwsFullName" type="text" placeholder="Full name" style="margin-bottom:10px;"></div>' +
              '<div class="bnws-reveal" id="bnwsRevealEmail"><input class="bnws-input" id="bnwsEmail" type="email" placeholder="Email (for prepaid orders)"></div>' +
              '<p class="bnws-error" id="bnwsAddrError"></p>' +
              '<button type="button" class="bnws-btn" id="bnwsSaveAddress" disabled>Save &amp; Continue</button>' +
            '</div>' +

            '<div class="bnws-panel bnws-panel-payment">' +
              '<div class="bnws-mini" id="bnwsMiniPay"></div>' +
              '<p class="bnws-trust bnws-care-line">🌱 This purchase is part of Braclo Care — we contribute ¼ of our profits to grassroots good.</p>' +

              '<div class="bnws-coupon-card">' +
                '<div class="bnws-coupon-row">' +
                  '<span class="bnws-coupon-icon">%</span>' +
                  '<input type="text" id="bnwsCouponInput" placeholder="Coupon Code" autocomplete="off">' +
                  '<button type="button" class="bnws-coupon-apply" id="bnwsApplyCouponBtn">Apply</button>' +
                '</div>' +
                '<p class="bnws-coupon-result" id="bnwsCouponResult"></p>' +
              '</div>' +

              '<div class="bnws-coins-card" id="bnwsCoinsCard" style="display:none;">' +
                '<div class="bnws-coins-row">' +
                  '<span class="bnws-coins-icon"><span class="bnws-coins-icon-coin">' + ICONS.bcoin + '</span></span>' +
                  '<div class="bnws-coins-info"><div class="bnws-coins-title">Use your B Coins</div><div class="bnws-coins-sub" id="bnwsCoinsBalanceText"></div></div>' +
                  '<button type="button" class="bnws-coins-redeem" id="bnwsRedeemCoinsBtn">Redeem</button>' +
                '</div>' +
                '<div class="bnws-coins-applied" id="bnwsCoinsApplied"><span>' + ICONS.check + ' <span id="bnwsCoinsAppliedText"></span> applied</span><button type="button" class="bnws-coins-remove" id="bnwsRemoveCoinsBtn">Remove</button></div>' +
                '<p class="bnws-error" id="bnwsCoinsError"></p>' +
              '</div>' +

              '<h2 class="bnws-h2">Choose payment method</h2>' +

              '<div class="bnws-pm" data-pay="upi">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon"><span class="bnws-upi-icon-flash">' + ICONS.upi + '</span></div><div class="bnws-pm-info"><div class="bnws-pm-label">UPI</div><div class="bnws-pm-sub" id="bnwsPmSubUpi">Pay instantly using UPI</div></div><div class="bnws-pm-price" id="bnwsPmPriceUpi"></div><div class="bnws-pm-radio"></div></div>' +
                '<div class="bnws-pm-panel" id="bnwsUpiPanel">' +
                  '<div class="bnws-upi-apps">' +
                    '<button type="button" class="bnws-upi-app" data-app="phonepe"><span class="bnws-upi-app-mount" id="bnwsUpiMountPhonepe"></span><span class="bnws-upi-app-logo">' + ICONS.phonepe + '</span><span class="bnws-upi-app-name">PhonePe</span></button>' +
                    '<button type="button" class="bnws-upi-app" data-app="gpay"><span class="bnws-upi-app-mount" id="bnwsUpiMountGpay"></span><span class="bnws-upi-app-logo">' + ICONS.gpay + '</span><span class="bnws-upi-app-name">G Pay</span></button>' +
                    '<button type="button" class="bnws-upi-app" data-app="paytm"><span class="bnws-upi-app-mount" id="bnwsUpiMountPaytm"></span><span class="bnws-upi-app-logo">' + ICONS.paytm + '</span><span class="bnws-upi-app-name">Paytm</span></button>' +
                  '</div>' +
                  '<button type="button" class="bnws-add-new" id="bnwsUpiIdToggle">' + ICONS.plus + ' Add UPI ID</button>' +
                  '<div id="bnwsUpiIdPanel" hidden>' +
                    '<label class="bnws-field-label">UPI ID</label>' +
                    '<div class="bnws-cf-mount" id="bnwsUpiCollectMount"></div>' +
                  '</div>' +
                  '<p class="bnws-error" id="bnwsUpiError"></p>' +
                '</div>' +
              '</div>' +

              '<div class="bnws-pm" data-pay="card">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">' + ICONS.card + '</div><div class="bnws-pm-info"><div class="bnws-pm-label">Cards</div><div class="bnws-pm-sub" id="bnwsCardSub">Visa · Mastercard · RuPay</div></div><div class="bnws-pm-price" id="bnwsPmPriceCard"></div><div class="bnws-pm-radio"></div></div>' +
                '<div class="bnws-pm-panel" id="bnwsCardPanel">' +
                  '<label class="bnws-field-label">Card Number</label><div class="bnws-cf-mount" id="bnwsCardNumberMount"></div>' +
                  '<label class="bnws-field-label">Name on Card</label><div class="bnws-cf-mount" id="bnwsCardHolderMount"></div>' +
                  '<div class="bnws-row2">' +
                    '<div><label class="bnws-field-label">Expiry</label><div class="bnws-cf-mount" id="bnwsCardExpiryMount"></div></div>' +
                    '<div><label class="bnws-field-label">CVV</label><div class="bnws-cf-mount" id="bnwsCardCvvMount"></div></div>' +
                  '</div>' +
                  '<p class="bnws-error" id="bnwsCardError"></p>' +
                  '<button type="button" id="bnwsAmexPay" hidden style="display:block;margin:10px 0 0;background:none;border:0;padding:4px 0;font:inherit;font-size:13px;font-weight:600;color:#111010;text-decoration:underline;cursor:pointer">Amex or Diners Club card? Pay here →</button>' +
                '</div>' +
              '</div>' +

              '<div class="bnws-pm" data-pay="netbanking">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">🏦</div><div class="bnws-pm-info"><div class="bnws-pm-label">Net Banking</div><div class="bnws-pm-sub">All major banks</div></div><div class="bnws-pm-price" id="bnwsPmPriceNetbanking"></div><div class="bnws-pm-radio"></div></div>' +
              '</div>' +

              '<div class="bnws-pm" data-pay="wallet">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">👛</div><div class="bnws-pm-info"><div class="bnws-pm-label">Wallets</div><div class="bnws-pm-sub">PhonePe · Amazon Pay · MobiKwik</div></div><div class="bnws-pm-price" id="bnwsPmPriceWallet"></div><div class="bnws-pm-radio"></div></div>' +
              '</div>' +

              '<div class="bnws-pm" data-pay="paylater" id="bnwsPmPaylaterRow" style="display:none">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">🕑</div><div class="bnws-pm-info"><div class="bnws-pm-label">Pay Later</div><div class="bnws-pm-sub">Buy now, pay later</div></div><div class="bnws-pm-price" id="bnwsPmPricePaylater"></div><div class="bnws-pm-radio"></div></div>' +
              '</div>' +

              '<div class="bnws-pm" data-pay="amex" id="bnwsPmAmexRow" style="display:none">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">' + ICONS.card + '</div><div class="bnws-pm-info"><div class="bnws-pm-label">Amex &amp; Diners Club</div><div class="bnws-pm-sub">American Express and Diners Club cards</div></div><div class="bnws-pm-price" id="bnwsPmPriceAmex"></div><div class="bnws-pm-radio"></div></div>' +
              '</div>' +
              '<div class="bnws-pm-panel" id="bnwsWlPanel"><div id="bnwsWlEmbed"></div></div>' +

              '<div class="bnws-pm" data-pay="cod">' +
                '<div class="bnws-pm-head"><div class="bnws-pm-icon">' + ICONS.cod + '</div><div class="bnws-pm-info"><div class="bnws-pm-label">Cash on Delivery</div><div class="bnws-pm-sub" id="bnwsCodSub">Pay when your order arrives</div></div><div class="bnws-pm-price" id="bnwsPmPriceCod"></div><div class="bnws-pm-radio"></div></div>' +
              '</div>' +

              '<p class="bnws-error" id="bnwsPrepaidError"></p>' +
              '<div class="bnws-recovery-card" id="bnwsPaymentRecovery">' +
                '<p class="bnws-recovery-title">That didn\'t go through.</p>' +
                '<p class="bnws-recovery-sub">Your order is still saved — nothing was charged.</p>' +
                '<div class="bnws-recovery-actions">' +
                  '<button type="button" class="bnws-recovery-btn" id="bnwsRecoveryRetry">Try again →</button>' +
                  '<button type="button" class="bnws-recovery-btn-ghost" id="bnwsRecoverySwitch">Use another method →</button>' +
                '</div>' +
              '</div>' +
            '</div>' +

          '</div>' +

          '<div class="bnws-sheet-footer" style="flex:none;padding:14px 40px calc(12px + env(safe-area-inset-bottom));border-top:1px solid #e2ddd5;display:none;" id="bnwsFooter">' +
            '<button type="button" class="bnws-btn" id="bnwsPayBtn" style="margin-top:0;display:flex;justify-content:center;box-shadow:0 4px 14px -4px rgba(17,16,16,.35);" disabled>' +
              '<span id="bnwsPayLabel">Pay</span><span id="bnwsPayPrice" style="font-family:Jost,sans-serif;"></span>' +
            '</button>' +
          '</div>' +

        '</div>' +

        '<div class="bnws-processing" id="bnwsProcessingScreen">' +
          '<div class="bnws-proc-ring"><svg viewBox="0 0 76 76"><circle class="bnws-proc-track" cx="38" cy="38" r="32"/><circle class="bnws-proc-fill" id="bnwsProcFill" cx="38" cy="38" r="32"/></svg><div class="bnws-proc-icon" id="bnwsProcIcon">' + ICONS.lock + '</div></div>' +
          '<div class="bnws-proc-status" id="bnwsProcStatus">' +
            '<div class="bnws-proc-item" data-proc="pay"><span class="bnws-proc-dot">' + ICONS.check + '</span>Processing payment…</div>' +
            '<div class="bnws-proc-item" data-proc="verify"><span class="bnws-proc-dot">' + ICONS.check + '</span>Payment verified</div>' +
            '<div class="bnws-proc-item" data-proc="confirm"><span class="bnws-proc-dot">' + ICONS.check + '</span>Order confirmed</div>' +
          '</div>' +
        '</div>' +

        '<div class="bnws-success" id="bnwsSuccessScreen">' +
          '<div class="bnws-success-scroll">' +
            '<div class="bnws-check-wrap"><svg viewBox="0 0 44 44"><circle class="bnws-check-circle" cx="22" cy="22" r="12"/><path class="bnws-check-tick" d="M15 22.5l4.5 4.5 9.5-10"/></svg></div>' +
            '<h2 class="bnws-success-title">Order Placed! 🎉</h2>' +
            '<p class="bnws-success-sub">Your order has been confirmed.</p>' +
            '<div class="bnws-success-card">' +
              '<div class="bnws-success-row"><span class="bnws-success-label">Order</span><span class="bnws-success-value" id="bnwsOrderName"></span></div>' +
              '<div class="bnws-success-row" id="bnwsEtaRow" style="display:none;"><span class="bnws-success-label">Estimated Delivery</span><span class="bnws-success-value" id="bnwsEta"></span></div>' +
              '<div class="bnws-success-row"><span class="bnws-success-label">Paid via</span><span class="bnws-success-value" id="bnwsPaidVia"></span></div>' +
            '</div>' +
          '</div>' +
          '<div class="bnws-success-footer">' +
            '<button type="button" class="bnws-btn" id="bnwsGoConfirmation" style="margin-top:0;">View Order →</button>' +
            '<button type="button" class="bnws-btn-ghost" id="bnwsCloseSuccess">Continue Shopping</button>' +
          '</div>' +
        '</div>' +

        '<div class="bnws-modal-overlay" id="bnwsNbModalOverlay" hidden>' +
          '<div class="bnws-modal-sheet">' +
            '<div class="bnws-modal-header"><span>Net Banking</span><button type="button" class="bnws-modal-close" id="bnwsNbModalClose">' + ICONS.close + '</button></div>' +
            '<div class="bnws-modal-body">' +
              '<div class="bnws-modal-amount-row"><span>Amount to pay</span><strong id="bnwsNbModalAmount"></strong></div>' +
              '<div class="bnws-nb-search"><input type="text" id="bnwsNetbankingSearch" placeholder="Search bank" autocomplete="off"></div>' +
              '<div class="bnws-nb-filter-chips">' +
                '<button type="button" class="bnws-chip is-active" data-filter="all">All</button>' +
                '<button type="button" class="bnws-chip" data-filter="popular">Popular Banks</button>' +
                '<button type="button" class="bnws-chip" data-filter="other">Other Banks</button>' +
              '</div>' +
              '<div class="bnws-nb-list" id="bnwsNetbankingGrid"></div>' +
            '</div>' +
          '</div>' +
        '</div>' +

        '<div class="bnws-modal-overlay" id="bnwsWalletModalOverlay" hidden>' +
          '<div class="bnws-modal-sheet">' +
            '<div class="bnws-modal-header"><span id="bnwsWalletModalTitle">Wallets</span><button type="button" class="bnws-modal-close" id="bnwsWalletModalClose">' + ICONS.close + '</button></div>' +
            '<div class="bnws-modal-body">' +
              '<div class="bnws-modal-amount-row"><span>Amount to pay</span><strong id="bnwsWalletModalAmount"></strong></div>' +
              '<div class="bnws-nb-list" id="bnwsWalletGrid"></div>' +
            '</div>' +
          '</div>' +
        '</div>' +

      '</div>';
    document.body.appendChild(wrap.firstElementChild);

    els.overlay = document.getElementById('bnwsOverlay');
    els.sheet = document.getElementById('bnwsSheet');
    els.back = document.getElementById('bnwsBack');
    els.close = document.getElementById('bnwsClose');
    els.dots = document.getElementById('bnwsDots');
    els.phone = document.getElementById('bnwsPhone');
    els.phoneError = document.getElementById('bnwsPhoneError');
    els.sendOtp = document.getElementById('bnwsSendOtp');
    els.otpPanel = document.getElementById('bnwsOtpPanel');
    els.otpCode = document.getElementById('bnwsOtpCode');
    els.otpError = document.getElementById('bnwsOtpError');
    els.resendLink = document.getElementById('bnwsResendLink');
    els.verifyOtp = document.getElementById('bnwsVerifyOtp');
    els.miniAddr = document.getElementById('bnwsMiniAddr');
    els.miniPay = document.getElementById('bnwsMiniPay');
    els.addrList = document.getElementById('bnwsAddrList');
    els.addNewBtn = document.getElementById('bnwsAddNewBtn');
    els.usingPhone = document.getElementById('bnwsUsingPhone');
    els.pincode = document.getElementById('bnwsPincode');
    els.lookupNote = document.getElementById('bnwsLookupNote');
    els.revealCityState = document.getElementById('bnwsRevealCityState');
    els.revealAddr1 = document.getElementById('bnwsRevealAddr1');
    els.revealName = document.getElementById('bnwsRevealName');
    els.revealEmail = document.getElementById('bnwsRevealEmail');
    els.city = document.getElementById('bnwsCity');
    els.state = document.getElementById('bnwsState');
    els.address1 = document.getElementById('bnwsAddress1');
    els.fullName = document.getElementById('bnwsFullName');
    els.email = document.getElementById('bnwsEmail');
    els.addrError = document.getElementById('bnwsAddrError');
    els.saveAddress = document.getElementById('bnwsSaveAddress');
    els.footer = document.getElementById('bnwsFooter');
    els.payBtn = document.getElementById('bnwsPayBtn');
    els.payLabel = document.getElementById('bnwsPayLabel');
    els.payPrice = document.getElementById('bnwsPayPrice');
    els.codSub = document.getElementById('bnwsCodSub');
    els.prepaidError = document.getElementById('bnwsPrepaidError');
    els.welcomeBack = document.getElementById('bnwsWelcomeBack');
    els.welcomeName = document.getElementById('bnwsWelcomeName');
    els.deliveryCard = document.getElementById('bnwsDeliveryCard');
    els.deliveryDate = document.getElementById('bnwsDeliveryDate');
    els.paymentRecovery = document.getElementById('bnwsPaymentRecovery');
    els.recoveryRetry = document.getElementById('bnwsRecoveryRetry');
    els.recoverySwitch = document.getElementById('bnwsRecoverySwitch');
    els.upiError = document.getElementById('bnwsUpiError');
    els.upiIdToggle = document.getElementById('bnwsUpiIdToggle');
    els.upiIdPanel = document.getElementById('bnwsUpiIdPanel');
    els.cardError = document.getElementById('bnwsCardError');
    els.couponInput = document.getElementById('bnwsCouponInput');
    els.applyCouponBtn = document.getElementById('bnwsApplyCouponBtn');
    els.couponResult = document.getElementById('bnwsCouponResult');
    els.coinsCard = document.getElementById('bnwsCoinsCard');
    els.coinsBalanceText = document.getElementById('bnwsCoinsBalanceText');
    els.redeemCoinsBtn = document.getElementById('bnwsRedeemCoinsBtn');
    els.coinsApplied = document.getElementById('bnwsCoinsApplied');
    els.coinsAppliedText = document.getElementById('bnwsCoinsAppliedText');
    els.removeCoinsBtn = document.getElementById('bnwsRemoveCoinsBtn');
    els.coinsError = document.getElementById('bnwsCoinsError');
    els.pmSubUpi = document.getElementById('bnwsPmSubUpi');
    els.pmPriceUpi = document.getElementById('bnwsPmPriceUpi');
    els.pmPriceCard = document.getElementById('bnwsPmPriceCard');
    els.pmPriceNetbanking = document.getElementById('bnwsPmPriceNetbanking');
    els.pmPriceWallet = document.getElementById('bnwsPmPriceWallet');
    els.pmPricePaylater = document.getElementById('bnwsPmPricePaylater');
    els.pmPaylaterRow = document.getElementById('bnwsPmPaylaterRow');
    els.pmWorldlineRow = document.getElementById('bnwsPmWorldlineRow');
    els.pmPriceWorldline = document.getElementById('bnwsPmPriceWorldline');
    els.pmAmexRow = document.getElementById('bnwsPmAmexRow');
    els.pmPriceAmex = document.getElementById('bnwsPmPriceAmex');
    els.walletModalTitle = document.getElementById('bnwsWalletModalTitle');
    els.pmPriceCod = document.getElementById('bnwsPmPriceCod');
    els.nbModalOverlay = document.getElementById('bnwsNbModalOverlay');
    els.nbModalAmount = document.getElementById('bnwsNbModalAmount');
    els.nbModalClose = document.getElementById('bnwsNbModalClose');
    els.netbankingSearch = document.getElementById('bnwsNetbankingSearch');
    els.netbankingGrid = document.getElementById('bnwsNetbankingGrid');
    els.walletModalOverlay = document.getElementById('bnwsWalletModalOverlay');
    els.walletModalAmount = document.getElementById('bnwsWalletModalAmount');
    els.walletModalClose = document.getElementById('bnwsWalletModalClose');
    els.walletGrid = document.getElementById('bnwsWalletGrid');
    els.processingScreen = document.getElementById('bnwsProcessingScreen');
    els.successScreen = document.getElementById('bnwsSuccessScreen');
    els.procFill = document.getElementById('bnwsProcFill');
    els.procStatus = document.getElementById('bnwsProcStatus');
    els.procIcon = document.getElementById('bnwsProcIcon');
    els.orderName = document.getElementById('bnwsOrderName');
    els.etaRow = document.getElementById('bnwsEtaRow');
    els.eta = document.getElementById('bnwsEta');
    els.paidVia = document.getElementById('bnwsPaidVia');
    els.goConfirmation = document.getElementById('bnwsGoConfirmation');
    els.closeSuccess = document.getElementById('bnwsCloseSuccess');

    wireEvents();
  }

  var state = {
    variantId: null, qty: 1, productTitle: '', variantTitle: '', unitPricePaise: 0, image: '',
    verificationToken: null, verifiedPhone: null, addresses: [], selectedAddrIdx: null,
    codServiceable: null, estimatedDays: null, selectedPay: null,
    cashfreeInstance: null, mountedComponents: [], upiCollectComponent: null, cardNumberComponent: null,
    upiReady: false, cardReady: false, cardFieldsComplete: { number: false, holder: false, expiry: false, cvv: false },
    currentPaymentSessionId: null, currentCfEnv: null, prepaidOrderId: null, prepaidPollTimer: null, prepaidPollAttempts: 0,
    cardElementsMounted: false, netbankingElementsMounted: false, walletElementsMounted: false,
    appliedDiscountCode: null, appliedDiscountAmount: 0, appliedCouponCode: null,
    netbankingActiveFilter: 'all', lastFailedMethod: null, productHandle: null
  };
  // 16px, not 14 - every plain HTML input on the full checkout page is
  // already pinned to 16px specifically because iOS Safari auto-zooms the
  // whole page on focus for any input rendered below that size. These
  // Cashfree Elements render inside their own iframe, but the same browser
  // rule still applies based on the focused field's own font-size, so this
  // one at 14px was the one input style still triggering it here too - a
  // customer typing their UPI ID or card details got the whole sheet
  // zoomed in as soon as they tapped the field.
  var cfFieldStyle = { base: { color: '#171717', fontSize: '16px', fontFamily: 'Poppins, sans-serif' } };

  var COD_FEE_THRESHOLD = 990;
  var COD_FEE_AMOUNT = 100;
  var PREPAID_AUTO_DISCOUNT_AMOUNT = 50;
  var ACTIVE_CHECKOUT_KEY = 'braclo_ckv2_active_checkout';
  var LAST_PM_KEY = 'braclo_ckv2_last_pm';

  var NETBANKING_BANKS = [
    { code: 'AUBLR', name: 'AU Small Finance Bank' },
    { code: 'AIRPR', name: 'Airtel Payments Bank' },
    { code: 'APGBR', name: 'Andhra Pragathi Grameena Bank' },
    { code: 'UTIBR', name: 'Axis Bank' },
    { code: 'UTIBC', name: 'Axis Bank - Corporate' },
    { code: 'BDBLR', name: 'Bandhan Bank - Retail Banking' },
    { code: 'BDBLC', name: 'Bandhan Bank - Corporate Banking' },
    { code: 'BBKMR', name: 'Bank of Bahrain and Kuwait' },
    { code: 'BARBC', name: 'Bank of Baroda - Corporate' },
    { code: 'BARBR', name: 'Bank of Baroda - Retail Banking' },
    { code: 'BKIDR', name: 'Bank of India' },
    { code: 'BKIDC', name: 'Bank of India - Corporate' },
    { code: 'MAHBR', name: 'Bank of Maharashtra' },
    { code: 'BARCC', name: 'Barclays Corporate - Corporate Banking' },
    { code: 'BACBC', name: 'Bassien Catholic Co-operative Bank' },
    { code: 'CSBKR', name: 'CSB Bank Limited' },
    { code: 'CNRBR', name: 'Canara Bank' },
    { code: 'CLBLR', name: 'Capital Small Finance Bank' },
    { code: 'CBINR', name: 'Central Bank of India' },
    { code: 'CIUBR', name: 'City Union Bank' },
    { code: 'CIUBC', name: 'City Union Bank - Corporate' },
    { code: 'COSBR', name: 'Cosmos Bank' },
    { code: 'DBSSR', name: 'DBS Bank Ltd' },
    { code: 'DCBLC', name: 'DCB Bank - Corporate' },
    { code: 'DCBLR', name: 'DCB Bank - Personal' },
    { code: 'DEUTR', name: 'Deutsche Bank' },
    { code: 'DLXBR', name: 'Dhanlaxmi Bank' },
    { code: 'DLXBC', name: 'Dhanlaxmi Bank - Corporate' },
    { code: 'ESMFR', name: 'ESAF Small Finance Bank' },
    { code: 'ESFBR', name: 'Equitas Small Finance Bank' },
    { code: 'FDRLR', name: 'Federal Bank' },
    { code: 'FSFBR', name: 'Fincare Bank' },
    { code: 'GSCBR', name: 'Gujarat State Co-operative Bank Limited' },
    { code: 'HDFCR', name: 'HDFC Bank' },
    { code: 'HDFCC', name: 'HDFC Bank - Corporate' },
    { code: 'HSBCR', name: 'HSBC Retail NetBanking' },
    { code: 'ICICR', name: 'ICICI Bank' },
    { code: 'ICICC', name: 'ICICI Bank - Corporate NetBanking' },
    { code: 'IBKLR', name: 'IDBI Bank' },
    { code: 'IDFBR', name: 'IDFC FIRST Bank' },
    { code: 'IDIBR', name: 'Indian Bank' },
    { code: 'IOBAR', name: 'Indian Overseas Bank' },
    { code: 'IOBAC', name: 'Indian Overseas Bank - Corporate' },
    { code: 'INDBR', name: 'IndusInd Bank' },
    { code: 'JAKAR', name: 'Jammu and Kashmir Bank' },
    { code: 'JSFBR', name: 'Jana Small Finance Bank' },
    { code: 'JSBPR', name: 'Janata Sahakari Bank Ltd Pune' },
    { code: 'JIOPR', name: 'Jio Payments Bank' },
    { code: 'KJSBR', name: 'Kalyan Janata Sahakari Bank' },
    { code: 'KARBR', name: 'Karnataka Bank Ltd' },
    { code: 'PKGBR', name: 'Karnataka Gramin Bank' },
    { code: 'KVGBR', name: 'Karnataka Vikas Grameena Bank' },
    { code: 'KVBLR', name: 'Karur Vysya Bank' },
    { code: 'KKBKR', name: 'Kotak Mahindra Bank' },
    { code: 'LAVBC', name: 'Lakshmi Vilas Bank - Corporate' },
    { code: 'LAVBR', name: 'Lakshmi Vilas Bank - Retail NetBanking' },
    { code: 'MAHGR', name: 'Maharashtra Gramin Bank' },
    { code: 'MSNUR', name: 'Mehsana Urban Co-operative Bank' },
    { code: 'NKGSR', name: 'NKGSB Co-operative Bank' },
    { code: 'NESFR', name: 'North East Small Finance Bank Ltd' },
    { code: 'NNSBR', name: 'Nutan Nagarik Sahakari Bank Limited' },
    { code: 'PSIBR', name: 'Punjab & Sind Bank' },
    { code: 'PUNBC', name: 'Punjab National Bank - Corporate' },
    { code: 'PUNBR', name: 'Punjab National Bank - Retail Banking' },
    { code: 'RATNR', name: 'RBL Bank' },
    { code: 'RATNC', name: 'RBL Bank - Corporate Banking' },
    { code: 'STCBR', name: 'SBM Bank India' },
    { code: 'SRCBR', name: 'Saraswat Bank' },
    { code: 'SVCBC', name: 'Shamrao Vithal Bank - Corporate' },
    { code: 'SVCBR', name: 'Shamrao Vithal Co-operative Bank' },
    { code: 'SMCBR', name: 'Shivalik Bank' },
    { code: 'SIBLR', name: 'South Indian Bank' },
    { code: 'SCBLR', name: 'Standard Chartered Bank' },
    { code: 'SBINR', name: 'State Bank of India' },
    { code: 'SBINC', name: 'State Bank of India - Corporate' },
    { code: 'SURYR', name: 'Suryoday Small Finance Bank' },
    { code: 'TJSBR', name: 'TJSB Bank' },
    { code: 'TNSCR', name: 'Tamil Nadu State Co-operative Bank' },
    { code: 'TMBLR', name: 'Tamilnad Mercantile Bank Ltd' },
    { code: 'TBSBR', name: "Thane Bharat Sahakari Bank Ltd" },
    { code: 'KCCBR', name: 'The Kalupur Commercial Co-operative Bank' },
    { code: 'SPCBR', name: "The Surat People's Co-operative Bank Limited" },
    { code: 'SUTBR', name: 'The Sutex Co-operative Bank Ltd' },
    { code: 'UCBAR', name: 'UCO Bank' },
    { code: 'UCBAC', name: 'UCO Bank - Corporate' },
    { code: 'UBINR', name: 'Union Bank of India' },
    { code: 'UBINC', name: 'Union Bank of India - Corporate' },
    { code: 'UTKSR', name: 'Utkarsh Small Finance Bank' },
    { code: 'VARAR', name: 'Varachha Co-operative Bank Limited' },
    { code: 'YESBC', name: 'Yes Bank - Corporate' },
    { code: 'YESBR', name: 'Yes Bank Ltd' },
    { code: 'ZCBLR', name: 'Zoroastrian Co-operative Bank Ltd' },
  ];
  var NETBANKING_POPULAR_CODES = ['HDFCR', 'ICICR', 'SBINR', 'UTIBR', 'KKBKR', 'IDFBR', 'PUNBR', 'BARBR', 'CNRBR', 'YESBR', 'INDBR', 'RATNR'];
  // Synced with Cashfree eligibility 2026-09-25 (see loadActivePaymentMethods /
  // applyActiveMethods below, same pattern as braclo-checkout-v2-page.js) -
  // both lists start empty and are populated per-load from the live
  // /payment-methods API instead of a static list (which still showed a
  // deactivated Paytm wallet).
  var WALLET_PROVIDERS = [];
  var PAYLATER_PROVIDERS = [];
  var CF_WALLET_SDK = { phonepe: 'phonepe', paytm: 'paytm', amazonpay: 'amazon', amazon: 'amazon', mobikwik: 'mobikwik', olamoney: 'ola', ola: 'ola', airtelmoney: 'airtel', airtel: 'airtel', freecharge: 'freecharge', jiomoney: 'jio', jio: 'jio' };
  var CF_PAYLATER_SDK = { lazypay: 'lazypay', epaylater: 'epaylater', freecharge: 'freecharge', simpl: 'simpl', olapostpaid: 'olapostpaid', zestmoney: 'zestmoney', hdfc: 'hdfc', kotak: 'kotak' };
  var CF_BANK_ALIAS = { dhanlakshmibank: 'DLXBR', laxmivilasbankretailnetbanking: 'LAVBR', icicicorporatenetbanking: 'ICICC' };
  var ALL_NETBANKING_BANKS = NETBANKING_BANKS;
  var activeMethodsAmount = null;
  function normKey(s){ return String(s || '').toLowerCase().replace(/[^a-z]/g, ''); }
  function normBankName(s){ return String(s || '').toLowerCase().replace(/&/g, 'and').replace(/\b(ltd|limited)\b/g, '').replace(/[^a-z0-9]/g, ''); }
  function sdkProviders(list, table, label){
    var out = [];
    (list || []).forEach(function(x){
      var key = normKey(x.nick), sdk = table[key] || table[normKey(x.name)];
      if (sdk) out.push({ code: sdk, name: x.name });
      else console.warn('[bnws] ' + label + ' "' + x.name + '" is active on Cashfree but has no SDK mapping yet - not shown');
    });
    return out;
  }
  function applyActiveMethods(m){
    var bankKeys = {}, aliasCodes = {};
    (m.netbanking || []).forEach(function(b){
      var k = normBankName(b.name);
      bankKeys[k] = true;
      if (CF_BANK_ALIAS[k]) aliasCodes[CF_BANK_ALIAS[k]] = k;
    });
    NETBANKING_BANKS = ALL_NETBANKING_BANKS.filter(function(b){
      var k = normBankName(b.name);
      return bankKeys[k] || aliasCodes[b.code];
    });
    WALLET_PROVIDERS = sdkProviders(m.wallet, CF_WALLET_SDK, 'wallet');
    PAYLATER_PROVIDERS = sdkProviders(m.paylater, CF_PAYLATER_SDK, 'pay later');
    if (els.pmPaylaterRow) els.pmPaylaterRow.style.display = PAYLATER_PROVIDERS.length ? '' : 'none';
  }
  function loadActivePaymentMethods(){
    var amount = Math.max(1, Math.round((lineTotal() - PREPAID_AUTO_DISCOUNT_AMOUNT * 100) / 100));
    if (activeMethodsAmount === amount) return;
    activeMethodsAmount = amount;
    apiFetch('/payment-methods?amount=' + amount).then(applyActiveMethods).catch(function(err){
      activeMethodsAmount = null;
      console.error('[bnws] could not load active payment methods - showing full static lists', err);
    });
  }
  var BADGE_PALETTE = ['#c0392b', '#2980b9', '#8e44ad', '#16a085', '#d35400', '#2c3e50', '#27ae60', '#e67e22', '#34495e', '#7f8c8d', '#c2185b', '#00838f'];
  function badgeColorFor(name){
    var hash = 0;
    for (var i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return BADGE_PALETTE[hash % BADGE_PALETTE.length];
  }
  function badgeInitial(name){
    var m = name.match(/[A-Za-z]/);
    return m ? m[0].toUpperCase() : '?';
  }
  function badgeHtml(name){
    return '<span class="bnws-nb-row-logo" style="background:' + badgeColorFor(name) + ';color:#fff;">' + badgeInitial(name) + '</span>';
  }

  function lineTotal(){ return state.unitPricePaise * state.qty; }

  function renderMini(targetEl){
    if (!targetEl) return;
    targetEl.innerHTML =
      '<img class="bnws-mini-thumb" src="' + (state.image || '') + '" alt="">' +
      '<div class="bnws-mini-info"><div class="bnws-mini-name">' + escapeHtml(state.productTitle) + '</div><div class="bnws-mini-sub">' + escapeHtml(state.variantTitle) + ' · Qty ' + state.qty + '</div></div>' +
      '<div class="bnws-mini-price">' + formatMoney(lineTotal()) + '</div>';
  }

  function escapeHtml(s){
    return String(s || '').replace(/[&<>"']/g, function(c){ return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]; });
  }

  // Worldline (2nd gateway): modes Cashfree lacks + a backup when Cashfree fails. Shown only when the backend turns it on
  // for this shopper's number (WORLDLINE_ENABLED / WORLDLINE_TEST_PHONES on the server).
  function checkWorldline(){
    var phone = String(state.verifiedPhone || '').replace(/\D/g, '').slice(-10);
    if (!phone || state.worldlineCheckedFor === phone) return;
    state.worldlineCheckedFor = phone;
    rawApiFetch('/api/checkout/worldline/availability?phone=' + encodeURIComponent(phone)).then(function(r){
      state.worldlineOk = !!(r && r.available);
      var amexBtn = document.getElementById('bnwsAmexPay'), cardSub = document.getElementById('bnwsCardSub');
      if (cardSub && state.worldlineOk) cardSub.textContent = 'Visa · Mastercard · RuPay · Amex · Diners';
    }).catch(function(){});
  }
  var worldlineReady = null;
  function loadWorldline(){
    if (worldlineReady) return worldlineReady;
    var add = function(src){ return new Promise(function(ok, no){ var el = document.createElement('script'); el.src = src; el.onload = ok; el.onerror = function(){ no(new Error('Could not load the payment window. Try again.')); }; document.head.appendChild(el); }); };
    worldlineReady = (window.jQuery ? Promise.resolve() : add('https://www.paynimo.com/paynimocheckout/client/lib/jquery.min.js'))
      .then(function(){ return window.jQuery.pnCheckout ? null : add('https://www.paynimo.com/paynimocheckout/server/lib/checkout.js'); })
      .catch(function(e){ worldlineReady = null; throw e; });
    return worldlineReady;
  }
  function payWithWorldline(mode){
    var email = els.email.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { flashError(els.prepaidError, 'Please add a valid email in the address step for prepaid payments.'); return; }
    clearError(els.prepaidError);
    ensureAudioUnlocked();
    els.payBtn.disabled = true;
    els.payLabel.textContent = 'Opening secure payment…';
    apiFetch('/worldline/session', {
      method: 'POST',
      body: {
        lineItems: [{ variantId: 'gid://shopify/ProductVariant/' + state.variantId, quantity: state.qty }],
        shippingAddress: Object.assign({}, currentShippingAddress(), { phone: state.verifiedPhone }),
        email: email,
        phone: state.verifiedPhone,
        verificationToken: state.verificationToken,
        discountCode: state.appliedDiscountCode || undefined,
      },
    }).then(function(session){
      return loadWorldline().then(function(){
        var c = session.checkout, done = false;
        var box = document.getElementById('bnwsWlEmbed'), panel = document.getElementById('bnwsWlPanel');
        if (panel) panel.classList.add('is-open');
        if (box) { box.innerHTML = ''; box.style.minHeight = '420px'; setTimeout(function(){ box.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); }
        window.jQuery.pnCheckout({
          tarCall: false,
          features: { enableAbortResponse: true, enableNewWindowFlow: false, enableExpressPay: false, enableInstrumentDeRegistration: false, enableMerTxnDetails: true, showPGResponseMsg: true },
          consumerData: {
            deviceId: 'WEBSH2', token: c.token, returnUrl: c.returnUrl, paymentMode: mode || 'all', checkoutElement: '#bnwsWlEmbed', merchantLogoUrl: 'https://www.braclo.in/cdn/shop/t/53/assets/apple-touch-icon.png', merchantId: c.merchantCode, currency: c.currency,
            consumerId: c.consumerId, consumerMobileNo: c.mobile, consumerEmailId: c.email, txnId: c.txnId,
            items: [{ itemId: c.schemeCode, amount: c.amount, comAmt: '0' }],
            customStyle: { PRIMARY_COLOR_CODE: '#111010', SECONDARY_COLOR_CODE: '#FFFFFF', BUTTON_COLOR_CODE_1: '#111010', BUTTON_COLOR_CODE_2: '#FFFFFF' },
            responseHandler: function(res){
              if (done) return; done = true;
              var t = (res && res.paymentMethod && res.paymentMethod.paymentTransaction) || {}, code = String(t.statusCode || '');
              if (code === '0300' || code === '0398' || code === '0396') {
                // the order is created only once the backend itself confirms the payment with Worldline
                stopPrepaidPolling();
                state.prepaidOrderId = session.orderId;
                goToStep('loading');
                beginPrepaidPolling(session.orderId);
              } else {
                updatePayFooter();
                showPaymentRecovery('worldline');
              }
            },
          },
        });
        els.payLabel.textContent = 'Complete your payment above ↑';
        els.payPrice.textContent = '';
        els.payBtn.disabled = true;
      });
    }).catch(function(err){
      flashError(els.prepaidError, err.message || 'Could not start the payment. Please try again.');
      updatePayFooter();
    });
  }

  function goToStep(step){
    if (step === 'payment') checkWorldline();
    els.sheet.dataset.step = step;
    els.processingScreen.classList.remove('is-shown');
    els.successScreen.classList.remove('is-shown');
    els.otpPanel.style.display = (step === 'phone' && els.otpPanel.dataset.shown === '1') ? 'block' : (step === 'phone' ? els.otpPanel.style.display : 'none');
    els.back.disabled = (step === 'phone');

    var group = (step === 'payment') ? 'payment' : 'address';
    els.dots.querySelectorAll('.bnws-dot').forEach(function(d){
      var name = d.getAttribute('data-dot');
      d.classList.toggle('is-active', name === group);
      d.classList.toggle('is-done', name === 'address' && group === 'payment');
    });

    if (step === 'phone' || step === 'loading' || step === 'address-new') {
      els.footer.style.display = 'none';
    } else if (step === 'address') {
      els.footer.style.display = 'block';
      els.payBtn.style.justifyContent = 'center';
      els.payLabel.textContent = 'Deliver Here →';
      els.payPrice.textContent = '';
      els.payBtn.disabled = state.selectedAddrIdx === null;
    } else if (step === 'payment') {
      els.payBtn.style.justifyContent = 'center';
      var lastPm = null;
      try { lastPm = localStorage.getItem(LAST_PM_KEY); } catch (e) {}
      selectPmRow((lastPm === 'card' || lastPm === 'upi') ? lastPm : 'upi');
    }
  }

  function updatePmPrices(){
    var totalPaise = lineTotal();
    var codFeePaise = totalPaise < COD_FEE_THRESHOLD * 100 ? COD_FEE_AMOUNT * 100 : 0;
    var codTotalPaise = Math.max(0, totalPaise - state.appliedDiscountAmount * 100) + codFeePaise;
    if (els.pmPriceCod) els.pmPriceCod.textContent = formatMoney(codTotalPaise);
    if (els.codSub) els.codSub.textContent = codFeePaise > 0 ? ('Pay when it arrives · ₹' + COD_FEE_AMOUNT + ' handling fee applies') : 'Pay when it arrives.';

    var usingBCoins = state.appliedDiscountAmount > 0;
    var prepaidDiscountPaise = (usingBCoins ? state.appliedDiscountAmount : PREPAID_AUTO_DISCOUNT_AMOUNT) * 100;
    var prepaidTotalPaise = Math.max(100, totalPaise - prepaidDiscountPaise);
    var prepaidPriceText = formatMoney(prepaidTotalPaise);
    [els.pmPriceUpi, els.pmPriceCard, els.pmPriceNetbanking, els.pmPriceWallet, els.pmPricePaylater, els.pmPriceWorldline, els.pmPriceAmex].forEach(function(el){ if (el) el.textContent = prepaidPriceText; });
    if (els.pmSubUpi) els.pmSubUpi.textContent = usingBCoins ? 'Fastest way to pay ⚡' : ('Save ₹' + PREPAID_AUTO_DISCOUNT_AMOUNT + ' instantly');
  }

  function updatePayFooter(){
    updatePmPrices();
    if (state.selectedPay === 'cod') {
      els.footer.style.display = 'block';
      els.payLabel.textContent = 'Place Order — COD';
      els.payPrice.textContent = els.pmPriceCod.textContent;
      els.payBtn.disabled = state.codServiceable === false;
    } else if (state.selectedPay === 'upi') {
      els.footer.style.display = 'block';
      els.payLabel.textContent = 'Pay via UPI ID';
      els.payPrice.textContent = els.pmPriceUpi.textContent;
      els.payBtn.disabled = !state.upiReady;
    } else if (state.selectedPay === 'worldline' || state.selectedPay === 'amex') {
      els.footer.style.display = 'block';
      els.payLabel.textContent = state.selectedPay === 'amex' ? 'Pay by card' : 'Pay securely';
      els.payPrice.textContent = (state.selectedPay === 'amex' ? els.pmPriceAmex : els.pmPriceWorldline) ? (state.selectedPay === 'amex' ? els.pmPriceAmex : els.pmPriceWorldline).textContent : '';
      els.payBtn.disabled = false;
    } else if (state.selectedPay === 'card') {
      els.footer.style.display = 'block';
      els.payLabel.textContent = 'Pay by Card';
      els.payPrice.textContent = els.pmPriceCard.textContent;
      els.payBtn.disabled = !state.cardReady;
    } else {
      els.footer.style.display = 'none';
    }
  }

  function selectPmRow(method){
    state.selectedPay = method;
    hidePaymentRecovery();
    document.querySelectorAll('.bnws-pm').forEach(function(p){ p.classList.toggle('is-selected', p.getAttribute('data-pay') === method); });
    document.querySelectorAll('.bnws-pm-panel').forEach(function(p){ p.classList.remove('is-open'); });
    var row = document.querySelector('.bnws-pm[data-pay="' + method + '"]');
    var panel = row && row.querySelector('.bnws-pm-panel');
    if (panel) panel.classList.add('is-open');
    updatePayFooter();
    if (method !== 'cod' && method !== 'worldline' && method !== 'amex') startPrepaidSession();
    if (state.cashfreeInstance) {
      if (method === 'card') mountCardElements();
      if (method === 'netbanking') openNetbankingModal();
      if (method === 'wallet') openWalletModal('wallet');
      if (method === 'paylater') openWalletModal('paylater');
    }
  }

  function flashError(el, msg){ el.textContent = msg; el.classList.add('is-visible'); }
  function clearError(el){ el.classList.remove('is-visible'); }

  // Smart Error Recovery - a declined/failed prepaid payment never means the
  // customer has to restart. We keep their address+cart state intact and
  // offer either an instant retry of the same method or a nudge to switch,
  // instead of a bare "Payment Failed" dead end.
  function showPaymentRecovery(method){
    state.lastFailedMethod = method;
    els.recoveryRetry.textContent = method === 'card' ? 'Try card again →' : (method === 'cod' || method === 'worldline') ? 'Try again →' : 'Try UPI again →';
    els.recoverySwitch.textContent = 'Use another method →';
    els.paymentRecovery.classList.add('is-visible');
    els.payBtn.disabled = false;
  }
  function hidePaymentRecovery(){
    els.paymentRecovery.classList.remove('is-visible');
    state.lastFailedMethod = null;
  }

  // Live Delivery UI - turns the serviceability check's estimatedDays into an
  // actual date range plus an illustrative Order/Packed/Shipped/Delivered
  // strip. Only "Order" is ever marked active here (the order doesn't exist
  // yet) - this is a preview of what tracking will look like, not a claim
  // about real shipment state.
  function formatDeliveryRange(days){
    var fmt = function(d){ return d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }); };
    var start = new Date(); start.setDate(start.getDate() + days);
    var end = new Date(); end.setDate(end.getDate() + days + 1);
    return fmt(start) + ' – ' + fmt(end);
  }
  function updateDeliveryCard(){
    if (state.estimatedDays && state.codServiceable !== false) {
      els.deliveryDate.textContent = formatDeliveryRange(state.estimatedDays);
      els.deliveryCard.hidden = false;
    } else {
      els.deliveryCard.hidden = true;
    }
  }

  // Hold My Checkout - persists just enough (variant + qty) so that if the
  // customer closes the sheet or backs out of the tab, the product page can
  // offer a one-tap "Continue checkout" that reopens the sheet, which then
  // auto-restores their verified session/address via restoreVerification().
  function saveActiveCheckout(){
    try { sessionStorage.setItem(ACTIVE_CHECKOUT_KEY, JSON.stringify({ variantId: state.variantId, qty: state.qty })); } catch (e) {}
  }
  function clearActiveCheckout(){
    try { sessionStorage.removeItem(ACTIVE_CHECKOUT_KEY); } catch (e) {}
  }
  function showSavedToast(){
    var el = document.getElementById('bnwsSavedToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'bnwsSavedToast';
      el.className = 'bnws-saved-toast';
      el.textContent = 'Checkout saved — come back anytime to finish your order.';
      document.body.appendChild(el);
    }
    el.classList.add('is-shown');
    clearTimeout(showSavedToast._t);
    showSavedToast._t = setTimeout(function(){ el.classList.remove('is-shown'); }, 3200);
  }

  function open(variantId, opts){
    opts = opts || {};
    injectStyle();
    buildDom();
    resetState();
    state.variantId = String(variantId);
    state.qty = opts.qty || 1;
    els.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    loadProduct();
    loadCashfreeSdk().catch(function(){});
    saveActiveCheckout();
    restoreVerification();
  }

  function saveVerification(){
    try {
      sessionStorage.setItem('braclo_ckv2_verification', JSON.stringify({ token: state.verificationToken, phone: state.verifiedPhone }));
    } catch (e) {}
  }

  function restoreVerification(){
    var saved = null;
    try {
      var raw = sessionStorage.getItem('braclo_ckv2_verification');
      if (raw) saved = JSON.parse(raw);
    } catch (e) {}
    if (!saved || !saved.token || !saved.phone) {
      // Remember me: a device verified before skips the OTP (POST /resume-session).
      var dev = null;
      try { dev = localStorage.getItem('central_ckv2_device'); } catch (e) {}
      if (!dev || state.resumeTried) { goToStep('phone'); return; }
      state.resumeTried = true;
      goToStep('loading');
      apiFetch('/resume-session', { method: 'POST', body: { deviceToken: dev } }).then(function(d){
        try { if (d.deviceToken) localStorage.setItem('central_ckv2_device', d.deviceToken); sessionStorage.setItem('braclo_ckv2_verification', JSON.stringify({ token: d.verificationToken, phone: d.phone })); } catch (e) {}
        restoreVerification();
      }).catch(function(){ try { localStorage.removeItem('central_ckv2_device'); } catch (e) {} goToStep('phone'); });
      return;
    }
    goToStep('loading');
    apiFetch('/addresses?verificationToken=' + encodeURIComponent(saved.token))
      .then(function(data){
        state.verificationToken = saved.token;
        state.verifiedPhone = saved.phone;
        els.usingPhone.textContent = saved.phone.slice(0, 5) + ' ' + saved.phone.slice(5);
        state.addresses = data.addresses || [];
        if (data.email) els.email.value = data.email;
        checkRewardsBalance();
        renderAddressList();
        showWelcomeBackIfReturning();
        maybeSendCartSnapshot();
        goToStep('address');
      })
      .catch(function(){
        try { sessionStorage.removeItem('braclo_ckv2_verification'); } catch (e) {}
        goToStep('phone');
      });
  }

  // Smart Checkout Memory - a customer with any saved address on file (from
  // this session's restore OR a fresh OTP verify that found existing Shopify
  // customer addresses) is a returning customer, not a stranger. Greet them
  // by name instead of dropping them into a blank "Where should we deliver?"
  // form they've already filled in before.
  function showWelcomeBackIfReturning(){
    if (state.addresses.length && state.addresses[0].firstName) {
      els.welcomeName.textContent = ', ' + state.addresses[0].firstName;
      els.welcomeBack.hidden = false;
    } else {
      els.welcomeBack.hidden = true;
    }
  }

  function close(){
    els.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    stopPrepaidPolling();
    try {
      if (state.verificationToken && sessionStorage.getItem(ACTIVE_CHECKOUT_KEY)) showSavedToast();
    } catch (e) {}
  }

  function resetState(){
    state.verificationToken = null; state.verifiedPhone = null; state.addresses = []; state.selectedAddrIdx = null;
    state.codServiceable = null; state.estimatedDays = null; state.selectedPay = null;
    state.appliedDiscountCode = null; state.appliedDiscountAmount = 0; state.appliedCouponCode = null;
    state.netbankingActiveFilter = 'all'; state.lastFailedMethod = null;
    resetPrepaidSession();
    els.phone.value = ''; els.otpCode.value = '';
    els.otpPanel.style.display = 'none'; els.otpPanel.dataset.shown = '0';
    els.sendOtp.style.display = ''; els.sendOtp.disabled = true; els.sendOtp.textContent = 'Send OTP';
    els.resendLink.disabled = false; els.resendLink.textContent = 'Resend OTP';
    clearError(els.phoneError); clearError(els.otpError); clearError(els.addrError);
    document.querySelectorAll('.bnws-pm').forEach(function(p){ p.classList.remove('is-selected'); });
    document.querySelectorAll('.bnws-pm-panel').forEach(function(p){ p.classList.remove('is-open'); });
    els.upiIdPanel.hidden = true; els.upiIdToggle.style.display = '';
    els.couponInput.value = ''; els.couponResult.textContent = ''; els.couponResult.classList.remove('is-ok', 'is-bad');
    els.coinsCard.style.display = 'none'; els.coinsApplied.classList.remove('is-shown');
    els.redeemCoinsBtn.style.display = 'inline-flex'; els.redeemCoinsBtn.disabled = false; els.redeemCoinsBtn.textContent = 'Redeem';
    clearError(els.coinsError);
    els.welcomeBack.hidden = true;
    els.deliveryCard.hidden = true;
    hidePaymentRecovery();
    document.querySelectorAll('#bnwsNbModalOverlay .bnws-chip').forEach(function(c){ c.classList.toggle('is-active', c.getAttribute('data-filter') === 'all'); });
  }

  function loadProduct(){
    var handle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return;
    state.productHandle = handle;
    fetch('/products/' + handle + '.js').then(function(r){ return r.json(); }).then(function(p){
      var variant = null;
      for (var i = 0; i < p.variants.length; i++) { if (String(p.variants[i].id) === state.variantId) { variant = p.variants[i]; break; } }
      if (!variant) variant = p.variants[0];
      state.productTitle = p.title;
      state.variantTitle = (variant.title && variant.title !== 'Default Title') ? variant.title : '';
      state.unitPricePaise = variant.price;
      loadActivePaymentMethods();
      state.image = (p.featured_image || (p.media && p.media[0] && p.media[0].src) || '');
      if (state.image && state.image.indexOf('http') !== 0) state.image = 'https:' + state.image;
      renderMini(els.miniAddr); renderMini(els.miniPay);
      maybeSendCartSnapshot();
    }).catch(function(){});
  }

  // Abandoned-checkout WhatsApp recovery - logs this session's cart against
  // the verified phone the moment BOTH the phone is verified and the
  // product has finished loading (whichever resolves second triggers this,
  // since open() kicks both off without waiting on each other). Fire-and-
  // forget: a failure here must never block or slow down checkout itself.
  // Re-called (not just once) after an address is selected/saved, so the
  // abandoned-cart record picks up a real shipping address once one
  // exists - the backend keeps the latest non-null address it's seen
  // (see routes/checkout.js's /cart-snapshot COALESCE), so an earlier
  // call here with no address yet can't erase a later one.
  function maybeSendCartSnapshot(){
    if (!state.verificationToken || !state.productTitle) return;
    var addr = state.addresses[state.selectedAddrIdx] || state.addresses[0] || null;
    apiFetch('/cart-snapshot', {
      method: 'POST',
      body: {
        verificationToken: state.verificationToken,
        items: [{
          variantId: state.variantId,
          title: state.productTitle + (state.variantTitle ? ' - ' + state.variantTitle : ''),
          quantity: state.qty,
          price: state.unitPricePaise / 100,
          image: state.image,
        }],
        firstName: (state.addresses[0] && state.addresses[0].firstName) || '',
        address: addr ? { firstName: addr.firstName, lastName: addr.lastName, address1: addr.address1, city: addr.city, province: addr.province, zip: addr.zip } : null,
        handle: state.productHandle || '',
      },
    }).catch(function(){});
  }

  function wirePhoneStep(){
    els.phone.addEventListener('input', function(){
      this.value = this.value.replace(/\D/g, '').slice(0, 10);
      els.sendOtp.disabled = this.value.length !== 10;
    });
    els.sendOtp.addEventListener('click', function(){
      clearError(els.phoneError);
      var phone = els.phone.value;
      if (phone.length !== 10) { flashError(els.phoneError, 'Enter a valid 10-digit mobile number.'); return; }
      els.sendOtp.disabled = true; els.sendOtp.textContent = 'Sending…';
      apiFetch('/send-otp', { method: 'POST', body: { phone: phone } })
        .then(function(){
          els.sendOtp.style.display = 'none';
          els.otpPanel.style.display = 'block'; els.otpPanel.dataset.shown = '1';
          els.otpCode.focus();
        })
        .catch(function(err){ flashError(els.phoneError, err.message); els.sendOtp.disabled = false; });
    });
    els.resendLink.addEventListener('click', function(){
      clearError(els.otpError);
      var phone = els.phone.value;
      if (phone.length !== 10) return;
      els.resendLink.disabled = true; els.resendLink.textContent = 'Sending…';
      apiFetch('/send-otp', { method: 'POST', body: { phone: phone } })
        .then(function(){
          els.resendLink.textContent = 'Sent ✓';
          setTimeout(function(){ els.resendLink.textContent = 'Resend OTP'; els.resendLink.disabled = false; }, 3000);
        })
        .catch(function(err){
          flashError(els.otpError, err.message);
          els.resendLink.textContent = 'Resend OTP'; els.resendLink.disabled = false;
        });
    });
    els.otpCode.addEventListener('input', function(){
      this.value = this.value.replace(/\D/g, '').slice(0, 6);
      els.verifyOtp.disabled = this.value.length !== 6;
    });
    els.verifyOtp.addEventListener('click', function(){
      clearError(els.otpError);
      var phone = els.phone.value, code = els.otpCode.value;
      if (code.length !== 6) { flashError(els.otpError, 'Enter the 6-digit code.'); return; }
      els.verifyOtp.disabled = true;
      apiFetch('/verify-otp', { method: 'POST', body: { phone: phone, code: code } })
        .then(function(data){
          state.verificationToken = data.verificationToken;
          state.verifiedPhone = phone;
          els.usingPhone.textContent = phone.slice(0, 5) + ' ' + phone.slice(5);
          try { if (data.deviceToken) localStorage.setItem('central_ckv2_device', data.deviceToken); if (data.accountToken && !localStorage.getItem('braclo_account_token')) localStorage.setItem('braclo_account_token', data.accountToken); } catch (e) {}
          saveVerification();
          checkRewardsBalance();
          return apiFetch('/addresses?verificationToken=' + encodeURIComponent(data.verificationToken))
            .then(function(addrData){ state.addresses = addrData.addresses || []; if (addrData.email) els.email.value = addrData.email; })
            .catch(function(){ state.addresses = []; });
        })
        .then(function(){
          renderAddressList();
          showWelcomeBackIfReturning();
          maybeSendCartSnapshot();
          goToStep('address');
        })
        .catch(function(err){ flashError(els.otpError, err.message); })
        .finally(function(){ els.verifyOtp.disabled = false; });
    });
  }

  function renderAddressList(){
    if (!state.addresses.length) {
      els.addrList.innerHTML = '';
      openAddNewAddress();
      return;
    }
    els.addrList.innerHTML = state.addresses.map(function(a, i){
      var sel = i === state.selectedAddrIdx ? ' is-selected' : '';
      var name = (a.firstName + ' ' + a.lastName).trim();
      var line1 = [a.address1, a.address2].filter(Boolean).join(', ');
      return '<div class="bnws-addr-card' + sel + '" data-idx="' + i + '">' +
        (a.isDefault ? '<div class="bnws-addr-tag">Default</div>' : '') +
        '<div class="bnws-addr-name">' + escapeHtml(name) + '</div>' +
        '<div class="bnws-addr-body">' + escapeHtml(line1) + '<br>' + escapeHtml(a.city) + ', ' + escapeHtml(a.province) + ' - ' + escapeHtml(a.zip) + '</div>' +
        '<div class="bnws-addr-radio">' + ICONS.check + '</div>' +
        '</div>';
    }).join('');
    els.addrList.querySelectorAll('.bnws-addr-card').forEach(function(card){
      card.addEventListener('click', function(){
        state.selectedAddrIdx = parseInt(card.getAttribute('data-idx'), 10);
        renderAddressList();
        checkAddressServiceability(state.addresses[state.selectedAddrIdx].zip);
        els.payBtn.disabled = false;
        maybeSendCartSnapshot();
      });
    });
  }

  function checkAddressServiceability(zip){
    if (!zip || zip.length !== 6) { els.deliveryCard.hidden = true; return; }
    rawApiFetch('/api/serviceability/check?pincode=' + zip).then(function(s){
      state.codServiceable = s.serviceable && s.codAvailable ? true : (s.serviceable === false || !s.codAvailable ? false : null);
      state.estimatedDays = s.estimatedDays || null;
      updateDeliveryCard();
    }).catch(function(){ state.codServiceable = null; els.deliveryCard.hidden = true; });
  }

  function openAddNewAddress(){
    els.pincode.value = ''; els.city.value = ''; els.state.value = ''; els.address1.value = ''; els.fullName.value = ''; els.email.value = els.email.value || '';
    els.lookupNote.innerHTML = ''; els.lookupNote.className = 'bnws-lookup-note';
    [els.revealCityState, els.revealAddr1, els.revealName, els.revealEmail].forEach(function(r){ r.classList.remove('is-shown'); });
    els.saveAddress.disabled = true;
    clearError(els.addrError);
    goToStep('address-new');
  }

  function checkNewAddressComplete(){
    var ok = els.pincode.value.length === 6 && els.address1.value.trim() && els.fullName.value.trim();
    els.saveAddress.disabled = !ok;
  }

  function wireAddressStep(){
    els.addNewBtn.addEventListener('click', openAddNewAddress);

    els.pincode.addEventListener('input', function(){
      this.value = this.value.replace(/\D/g, '').slice(0, 6);
      [els.revealCityState, els.revealAddr1, els.revealName, els.revealEmail].forEach(function(r){ r.classList.remove('is-shown'); });
      els.lookupNote.innerHTML = '';
      if (this.value.length === 6) {
        var pincode = this.value;
        els.lookupNote.className = 'bnws-lookup-note';
        els.lookupNote.textContent = 'Looking up…';
        Promise.all([
          rawApiFetch('/api/pincode/lookup?pincode=' + pincode).catch(function(){ return { found: false }; }),
          rawApiFetch('/api/serviceability/check?pincode=' + pincode).catch(function(){ return { serviceable: null, codAvailable: null, estimatedDays: null }; }),
        ]).then(function(results){
          var locality = results[0], serviceability = results[1];
          if (locality.found) { els.city.value = locality.district || ''; els.state.value = locality.state || ''; }
          state.codServiceable = serviceability.serviceable && serviceability.codAvailable ? true : (serviceability.serviceable === false || !serviceability.codAvailable ? false : null);
          state.estimatedDays = serviceability.estimatedDays || null;
          if (serviceability.serviceable === false) {
            els.lookupNote.className = 'bnws-lookup-note is-err';
            els.lookupNote.textContent = "We don't currently deliver to this pincode.";
          } else if (locality.found) {
            els.lookupNote.className = 'bnws-lookup-note';
            els.lookupNote.innerHTML = ICONS.check + ' ' + locality.district + ', ' + locality.state;
          } else {
            els.lookupNote.className = 'bnws-lookup-note is-err';
            els.lookupNote.textContent = 'Could not find this pincode — you can still continue.';
          }
          els.revealCityState.classList.add('is-shown');
          setTimeout(function(){ els.revealAddr1.classList.add('is-shown'); }, 200);
          setTimeout(function(){ els.revealName.classList.add('is-shown'); }, 380);
          setTimeout(function(){ els.revealEmail.classList.add('is-shown'); }, 560);
          checkNewAddressComplete();
        });
      }
      checkNewAddressComplete();
    });
    [els.address1, els.fullName].forEach(function(inp){ inp.addEventListener('input', checkNewAddressComplete); });

    els.saveAddress.addEventListener('click', function(){
      var parts = els.fullName.value.trim().split(/\s+/);
      var firstName = parts.shift() || '', lastName = parts.join(' ');
      var addr = { firstName: firstName, lastName: lastName, address1: els.address1.value.trim(), city: els.city.value.trim() || 'NA', province: els.state.value.trim() || 'NA', zip: els.pincode.value, country: 'India', isDefault: false };
      if (!addr.firstName || !addr.address1 || !addr.zip) { flashError(els.addrError, 'Please fill in the required fields.'); return; }
      state.addresses.push(addr);
      state.selectedAddrIdx = state.addresses.length - 1;
      renderAddressList();
      goToStep('address');
      els.payBtn.disabled = false;
      maybeSendCartSnapshot();
    });

    els.back.addEventListener('click', function(){
      var step = els.sheet.dataset.step;
      if (step === 'address') { close(); }
      else if (step === 'address-new') { state.addresses.length ? goToStep('address') : close(); }
      else if (step === 'payment') { goToStep('address'); }
      else if (step === 'otp' || (step === 'phone' && els.otpPanel.dataset.shown === '1')) { els.otpPanel.style.display = 'none'; els.otpPanel.dataset.shown = '0'; els.sendOtp.style.display = ''; }
    });
    els.close.addEventListener('click', close);
    els.overlay.addEventListener('click', function(e){ if (e.target === els.overlay) close(); });
  }

  function currentShippingAddress(){
    var a = state.addresses[state.selectedAddrIdx];
    return { firstName: a.firstName, lastName: a.lastName, address1: a.address1, city: a.city, province: a.province, zip: a.zip, country: 'India' };
  }

  function wireCouponStep(){
    els.applyCouponBtn.addEventListener('click', function(){
      var code = els.couponInput.value.trim().toUpperCase();
      els.couponResult.classList.remove('is-ok', 'is-bad');
      if (!code) return;
      els.applyCouponBtn.disabled = true;
      els.applyCouponBtn.textContent = 'Checking…';
      rawApiFetch('/api/checkout/coupon-preview?code=' + encodeURIComponent(code))
        .then(function(data){
          if (data.valid) {
            state.appliedCouponCode = code;
            els.couponResult.textContent = 'Applied: ' + data.summary + ' on Cash on Delivery orders.';
            els.couponResult.classList.add('is-ok');
          } else {
            state.appliedCouponCode = null;
            els.couponResult.textContent = 'This code is invalid or expired.';
            els.couponResult.classList.add('is-bad');
          }
        })
        .catch(function(){
          state.appliedCouponCode = null;
          els.couponResult.textContent = 'Could not check this code right now.';
          els.couponResult.classList.add('is-bad');
        })
        .finally(function(){
          els.applyCouponBtn.disabled = false;
          els.applyCouponBtn.textContent = 'Apply';
        });
    });
    els.couponInput.addEventListener('input', function(){
      state.appliedCouponCode = null;
      els.couponResult.classList.remove('is-ok', 'is-bad');
    });
  }

  function tierName(t){
    if (!t) return '';
    if (typeof t === 'string') return t;
    return t.name || t.tier || t.label || '';
  }

  function checkRewardsBalance(){
    apiFetch('/rewards-balance?verificationToken=' + encodeURIComponent(state.verificationToken))
      .then(function(data){
        if (!data.available) return;
        var tierLabel = tierName(data.tier);
        els.coinsBalanceText.textContent = data.balance + ' available' + (tierLabel ? ' · ' + tierLabel + ' tier' : '');
        els.coinsCard.style.display = 'block';
      })
      .catch(function(){});
  }

  function wireCoinsStep(){
    els.redeemCoinsBtn.addEventListener('click', function(){
      clearError(els.coinsError);
      els.redeemCoinsBtn.disabled = true;
      els.redeemCoinsBtn.textContent = 'Redeeming…';
      apiFetch('/redeem-coins', {
        method: 'POST',
        body: { verificationToken: state.verificationToken, cartSubtotal: lineTotal() / 100 },
      })
        .then(function(data){
          state.appliedDiscountCode = data.code;
          state.appliedDiscountAmount = data.amount;
          els.coinsAppliedText.textContent = formatMoney(data.amount * 100) + ' off';
          els.redeemCoinsBtn.style.display = 'none';
          els.coinsApplied.classList.add('is-shown');
          updatePmPrices();
          if (els.sheet.dataset.step === 'payment') updatePayFooter();
          resetPrepaidSession();
          if (state.selectedPay && state.selectedPay !== 'cod') startPrepaidSession();
        })
        .catch(function(err){ flashError(els.coinsError, err.message); })
        .finally(function(){
          els.redeemCoinsBtn.disabled = false;
          els.redeemCoinsBtn.textContent = 'Redeem';
        });
    });

    els.removeCoinsBtn.addEventListener('click', function(){
      state.appliedDiscountCode = null;
      state.appliedDiscountAmount = 0;
      els.coinsApplied.classList.remove('is-shown');
      els.redeemCoinsBtn.style.display = 'inline-flex';
      updatePmPrices();
      if (els.sheet.dataset.step === 'payment') updatePayFooter();
      resetPrepaidSession();
      if (state.selectedPay && state.selectedPay !== 'cod') startPrepaidSession();
    });
  }

  function netbankingFilteredList(filterText){
    var q = (filterText || '').trim().toLowerCase();
    if (q) return NETBANKING_BANKS.filter(function(b){ return b.name.toLowerCase().indexOf(q) !== -1; });
    if (state.netbankingActiveFilter === 'popular') return NETBANKING_BANKS.filter(function(b){ return NETBANKING_POPULAR_CODES.indexOf(b.code) !== -1; });
    if (state.netbankingActiveFilter === 'other') return NETBANKING_BANKS.filter(function(b){ return NETBANKING_POPULAR_CODES.indexOf(b.code) === -1; });
    return NETBANKING_BANKS;
  }

  function selectNetbankingBank(bank, row, mountEl){
    if (row.classList.contains('is-mounting') || row.classList.contains('is-unavailable')) return;
    row.classList.add('is-mounting');
    document.querySelectorAll('#bnwsNetbankingGrid .bnws-nb-row').forEach(function(r){ r.classList.remove('is-selected'); });
    row.classList.add('is-selected');
    var mountId = 'bnwsNbMount_' + bank.code + '_' + Date.now();
    mountEl.id = mountId;
    var comp = state.cashfreeInstance.create('netbanking', { values: { netbankingBankName: bank.code, buttonText: bank.name, buttonIcon: true } });
    comp.mount('#' + mountId);
    state.mountedComponents.push(comp);
    comp.on('loaderror', function(){
      row.classList.remove('is-mounting');
      row.classList.add('is-unavailable');
    });
    setTimeout(function(){
      if (row.classList.contains('is-unavailable')) return;
      payWithComponent(comp, els.prepaidError);
      closeNetbankingModal();
    }, 700);
  }

  function renderNetbankingBankRow(bank){
    var row = document.createElement('div');
    row.className = 'bnws-nb-row';
    row.innerHTML = '<div class="bnws-nb-row-mount"></div>' + badgeHtml(bank.name) + '<span class="bnws-nb-row-name"></span><span class="bnws-nb-row-arrow" aria-hidden="true">&rsaquo;</span>';
    row.querySelector('.bnws-nb-row-name').textContent = bank.name;
    var mountEl = row.querySelector('.bnws-nb-row-mount');
    row.addEventListener('click', function(){ selectNetbankingBank(bank, row, mountEl); });
    return row;
  }

  function renderNetbankingList(){
    els.netbankingGrid.innerHTML = '';
    var list = netbankingFilteredList(els.netbankingSearch.value);
    if (!list.length) {
      els.netbankingGrid.innerHTML = '<p style="font-size:.76rem;color:#8a7f74;padding:8px 2px;">No matching bank found.</p>';
      return;
    }
    list.forEach(function(bank){ els.netbankingGrid.appendChild(renderNetbankingBankRow(bank)); });
  }

  function openNetbankingModal(){
    if (els.pmPriceNetbanking) els.nbModalAmount.textContent = els.pmPriceNetbanking.textContent;
    els.nbModalOverlay.hidden = false;
    requestAnimationFrame(function(){ els.nbModalOverlay.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    renderNetbankingList();
  }

  function closeNetbankingModal(){
    els.nbModalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function(){ els.nbModalOverlay.hidden = true; }, 260);
  }

  function mountWalletElements(){
    if (state.walletElementsMounted) return;
    state.walletElementsMounted = true;
    els.walletGrid.innerHTML = '';
    var sheetKind = state.walletMountedKind === 'paylater' ? 'paylater' : 'wallet';
    var sheetList = sheetKind === 'paylater' ? PAYLATER_PROVIDERS : WALLET_PROVIDERS;
    sheetList.forEach(function(p, idx){
      var row = document.createElement('div');
      row.className = 'bnws-nb-row';
      var mountId = 'bnwsWalletMount_' + p.code;
      row.innerHTML = '<div class="bnws-nb-row-mount" id="' + mountId + '"></div>' + badgeHtml(p.name) + '<span class="bnws-nb-row-name"></span><span class="bnws-nb-row-arrow" aria-hidden="true">&rsaquo;</span>';
      row.querySelector('.bnws-nb-row-name').textContent = p.name;
      els.walletGrid.appendChild(row);
      setTimeout(function(){
        var comp = state.cashfreeInstance.create(sheetKind, { values: { provider: p.code, phone: state.verifiedPhone, buttonText: p.name, buttonIcon: true } });
        comp.mount('#' + mountId);
        state.mountedComponents.push(comp);
        row.addEventListener('click', function(){
          document.querySelectorAll('#bnwsWalletGrid .bnws-nb-row').forEach(function(c){ c.classList.remove('is-selected'); });
          row.classList.add('is-selected');
          payWithComponent(comp, els.prepaidError);
          closeWalletModal();
        });
        comp.on('loaderror', function(){ row.classList.add('is-unavailable'); });
      }, idx * 250);
    });
  }

  function openWalletModal(kind){
    kind = kind === 'paylater' ? 'paylater' : 'wallet';
    if (state.walletElementsMounted && state.walletMountedKind !== kind) {
      state.walletElementsMounted = false;
      if (els.walletGrid) els.walletGrid.innerHTML = '';
    }
    state.walletMountedKind = kind;
    if (els.walletModalTitle) els.walletModalTitle.textContent = kind === 'paylater' ? 'Pay Later' : 'Wallets';
    if (els.pmPriceWallet) els.walletModalAmount.textContent = els.pmPriceWallet.textContent;
    els.walletModalOverlay.hidden = false;
    requestAnimationFrame(function(){ els.walletModalOverlay.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    mountWalletElements();
  }

  function closeWalletModal(){
    els.walletModalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function(){ els.walletModalOverlay.hidden = true; }, 260);
  }

  function wireModals(){
    els.netbankingSearch.addEventListener('input', renderNetbankingList);
    document.querySelectorAll('#bnwsNbModalOverlay .bnws-chip').forEach(function(chip){
      chip.addEventListener('click', function(){
        document.querySelectorAll('#bnwsNbModalOverlay .bnws-chip').forEach(function(c){ c.classList.remove('is-active'); });
        chip.classList.add('is-active');
        state.netbankingActiveFilter = chip.getAttribute('data-filter');
        renderNetbankingList();
      });
    });
    els.nbModalClose.addEventListener('click', closeNetbankingModal);
    els.nbModalOverlay.addEventListener('click', function(e){ if (e.target === els.nbModalOverlay) closeNetbankingModal(); });
    els.walletModalClose.addEventListener('click', closeWalletModal);
    els.walletModalOverlay.addEventListener('click', function(e){ if (e.target === els.walletModalOverlay) closeWalletModal(); });
  }

  function wirePaymentStep(){
    wireCouponStep();
    wireCoinsStep();
    wireModals();
    // "+ Add UPI ID" starts collapsed - the upiCollect Element underneath is
    // already mounted regardless of this panel's visibility (see
    // mountCashfreeElementsNow), so revealing it here is purely a display
    // toggle, not a mount trigger.
    els.upiIdToggle.addEventListener('click', function(){
      var opening = els.upiIdPanel.hidden;
      els.upiIdPanel.hidden = !opening;
      els.upiIdToggle.style.display = opening ? 'none' : '';
      if (opening) mountUpiCollectLazy();
    });
    document.querySelectorAll('.bnws-pm').forEach(function(pm){
      pm.addEventListener('click', function(e){
        if (e.target.closest('.bnws-upi-app')) return;
        // Extra chance to unlock the success chime's AudioContext, on top
        // of the one in payWithComponent/placeCodOrder - a real user
        // gesture this early only helps, since some browsers are stricter
        // about only trusting an AudioContext resume() when it happens as
        // directly as possible after the actual tap.
        ensureAudioUnlocked();
        var method = pm.getAttribute('data-pay');
        selectPmRow(method);
        if (method === 'upi' || method === 'card') {
          try { localStorage.setItem(LAST_PM_KEY, method); } catch (err) {}
        }
      });
    });

    els.recoveryRetry.addEventListener('click', function(){
      var method = state.lastFailedMethod;
      hidePaymentRecovery();
      if (state.worldlineOk && (method === 'upi' || method === 'card')) { payWithWorldline(method === 'card' ? 'cards' : 'UPI'); return; }
      if (method === 'cod') { placeCodOrder(); }
      else if (method === 'upi' && state.upiCollectComponent) { payWithComponent(state.upiCollectComponent, els.upiError); }
      else if (method === 'card' && state.cardNumberComponent) { payWithComponent(state.cardNumberComponent, els.cardError); }
      else if (method === 'worldline') { payWithWorldline(); }
    });
    els.recoverySwitch.addEventListener('click', function(){
      hidePaymentRecovery();
      var otherMethod = state.lastFailedMethod === 'cod' ? 'upi' : 'cod';
      selectPmRow(otherMethod);
      document.querySelector('.bnws-h2').scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Amex / Diners (Cashfree does not take them): pay the card through Worldline, inline
    var amexPayBtn = document.getElementById('bnwsAmexPay');
    if (amexPayBtn) amexPayBtn.addEventListener('click', function(){ state.selectedPay = 'amex'; payWithWorldline('cards'); });

    els.payBtn.addEventListener('click', function(){
      if (els.sheet.dataset.step === 'address') {
        if (state.selectedAddrIdx === null) return;
        goToStep('payment');
        return;
      }
      clearError(els.prepaidError);
      if (state.selectedPay === 'cod') { placeCodOrder(); }
      else if (state.selectedPay === 'upi' && state.upiCollectComponent && state.upiReady) { payWithComponent(state.upiCollectComponent, els.upiError); }
      else if (state.selectedPay === 'card' && state.cardNumberComponent && state.cardReady) { payWithComponent(state.cardNumberComponent, els.cardError); }
      else if (state.selectedPay === 'worldline') { payWithWorldline(); }
      else if (state.selectedPay === 'amex') { payWithWorldline('cards'); }
    });

    els.goConfirmation.addEventListener('click', function(){
      window.location.href = '/pages/checkout-v2-confirmation?order=' + encodeURIComponent(state.orderId || '') + '&name=' + encodeURIComponent(state.orderName || '');
    });
    els.closeSuccess.addEventListener('click', close);
  }

  function placeCodOrder(){
    if (state.codServiceable === false) { flashError(els.prepaidError, 'Cash on Delivery is not available for this pincode.'); return; }
    ensureAudioUnlocked();
    els.payBtn.disabled = true;
    els.payLabel.textContent = 'Placing order…';
    apiFetch('/create-order', {
      method: 'POST',
      body: {
        lineItems: [{ variantId: 'gid://shopify/ProductVariant/' + state.variantId, quantity: state.qty }],
        shippingAddress: Object.assign({}, currentShippingAddress(), { phone: state.verifiedPhone }),
        email: els.email.value.trim(),
        phone: state.verifiedPhone,
        verificationToken: state.verificationToken,
        discountCode: state.appliedDiscountCode || undefined,
        couponCode: state.appliedCouponCode || undefined,
      },
    }).then(function(order){
      showSuccess(order.orderId, order.orderName, 'Cash on Delivery');
    }).catch(function(err){
      flashError(els.prepaidError, err.message);
      els.payBtn.disabled = false;
      updatePayFooter();
    });
  }

  function startPrepaidSession(){
    if (state.prepaidOrderId) return;
    var email = els.email.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      flashError(els.prepaidError, 'Please add a valid email in the address step for prepaid payments.');
      return;
    }
    clearError(els.prepaidError);
    apiFetch('/create-payment-session', {
      method: 'POST',
      body: {
        lineItems: [{ variantId: 'gid://shopify/ProductVariant/' + state.variantId, quantity: state.qty }],
        shippingAddress: Object.assign({}, currentShippingAddress(), { phone: state.verifiedPhone }),
        email: email,
        phone: state.verifiedPhone,
        verificationToken: state.verificationToken,
        discountCode: state.appliedDiscountCode || undefined,
      },
    }).then(function(session){
      state.prepaidOrderId = session.orderId;
      mountCashfreeElements(session.paymentSessionId, session.cfEnv);
      beginPrepaidPolling(session.orderId);
    }).catch(function(err){
      flashError(els.prepaidError, err.message);
    });
  }

  function payWithComponent(component, panelErrorEl){
    clearError(panelErrorEl);
    ensureAudioUnlocked();
    els.payBtn.disabled = true;
    // UPI/card failures get the full Smart Error Recovery card (retry the
    // same method or switch, without losing the address/cart state).
    // Netbanking/wallet failures (panelErrorEl === els.prepaidError, since
    // those flow through their own modal) keep the simpler inline message -
    // their retry path is reopening that modal, not a single retry button.
    var recoveryMethod = panelErrorEl === els.upiError ? 'upi' : panelErrorEl === els.cardError ? 'card' : null;
    state.cashfreeInstance.pay({
      paymentMethod: component,
      paymentSessionId: state.currentPaymentSessionId,
      returnUrl: window.location.origin + window.location.pathname + '?bnws_return=1&order_id={order_id}',
    }).then(function(result){
      if (result.error) {
        // The Smart Error Recovery banner (showPaymentRecovery) always shows
        // the same generic "That didn't go through" copy regardless of the
        // real reason - deliberately, so a customer isn't shown a raw
        // gateway error string - but that meant the actual reason (wrong
        // CVV, bank decline, insufficient funds, a client-side field state
        // bug, etc.) was never logged anywhere, on this path or the
        // .catch() below, making a real failure indistinguishable from a
        // client-side bug without asking the customer to reproduce it.
        console.error('[bnws] cashfree.pay result.error', result.error);
        if (recoveryMethod) { showPaymentRecovery(recoveryMethod); }
        else { flashError(panelErrorEl, result.error.message || 'Payment failed. Please try again.'); els.payBtn.disabled = false; }
        return;
      }
      flashError(panelErrorEl, 'Confirming your payment…');
      panelErrorEl.style.color = '#5a8a6a';
      // Cashfree's SDK just told us the charge went through - check right
      // now instead of waiting for the next scheduled 3s poll tick, so the
      // success screen appears the moment the backend can confirm it too,
      // not up to 3s later. Still backend-confirmed either way - never
      // trusting this client-side signal alone to show "Order Placed!".
      if (state.prepaidOrderId) checkPrepaidPaymentStatus(state.prepaidOrderId);
    }).catch(function(err){
      if (recoveryMethod) { showPaymentRecovery(recoveryMethod); }
      else { flashError(panelErrorEl, 'Payment failed. Please try again.'); els.payBtn.disabled = false; }
      console.error('[bnws] cashfree.pay error', err);
    });
  }

  function mountCashfreeElements(paymentSessionId, cfEnv){
    state.currentPaymentSessionId = paymentSessionId;
    state.currentCfEnv = cfEnv;
    loadCashfreeSdk().then(function(){ mountCashfreeElementsNow(cfEnv); }).catch(function(){
      flashError(els.prepaidError, 'Payment could not load. Please refresh, or choose Cash on Delivery.');
    });
  }

  function mountUpiCollectLazy(){
    if (state.upiCollectComponent || !state.cashfreeInstance) return;
    state.upiCollectComponent = state.cashfreeInstance.create('upiCollect', { values: {}, style: cfFieldStyle });
    state.upiCollectComponent.mount('#bnwsUpiCollectMount');
    state.mountedComponents.push(state.upiCollectComponent);
    state.upiCollectComponent.on('complete', function(){ state.upiReady = true; updatePayFooter(); });
    state.upiCollectComponent.on('empty', function(){ state.upiReady = false; updatePayFooter(); });
    state.upiCollectComponent.on('invalid', function(){ state.upiReady = false; updatePayFooter(); });
    state.upiCollectComponent.on('loaderror', function(){ flashError(els.upiError, 'UPI ID field could not load.'); });
  }

  function mountCashfreeElementsNow(cfEnv){
    if (!state.cashfreeInstance) state.cashfreeInstance = new window.Cashfree({ mode: cfEnv === 'production' ? 'production' : 'sandbox' });
    // UPI's own Elements mount eagerly below regardless of which method is
    // currently selected - they need to be ready the moment the customer
    // might switch to UPI. This used to force the whole UI back to the UPI
    // row via selectPmRow('upi') as a side effect of that eager mount,
    // which silently overwrote a Card selection remembered from a previous
    // order (see LAST_PM_KEY in goToStep) - the customer would see Card
    // silently un-select back to UPI, and since state.selectedPay was no
    // longer 'card' by the time this function reached its own "mount card
    // if selected" check below, Card's fields never mounted at all - its
    // Pay button stayed disabled forever unless the customer noticed and
    // manually tapped Card again (which happened to work, since
    // state.cashfreeInstance existed by then). Mounting UPI's elements
    // directly here, without touching state.selectedPay or the row
    // highlighting, fixes that.
    mountUpiCollectLazy();

    [
      { id: 'bnwsUpiMountPhonepe', app: 'phonepe' },
      { id: 'bnwsUpiMountGpay', app: 'gpay' },
      { id: 'bnwsUpiMountPaytm', app: 'paytm' },
    ].forEach(function(a, idx){
      var mountEl = document.getElementById(a.id);
      var card = mountEl.closest('.bnws-upi-app');
      var retried = false;
      function mountUpiApp(){
        var comp = state.cashfreeInstance.create('upiApp', { values: { upiApp: a.app }, buttonText: a.app, buttonIcon: true });
        comp.mount('#' + a.id);
        state.mountedComponents.push(comp);
        if (card) card.onclick = function(){
          document.querySelectorAll('.bnws-upi-app').forEach(function(c){ c.classList.remove('is-selected'); });
          card.classList.add('is-selected');
          payWithComponent(comp, els.upiError);
        };
        comp.on('loaderror', function(){
          if (!retried) { retried = true; mountEl.innerHTML = ''; setTimeout(mountUpiApp, 1200); return; }
          if (card) card.classList.add('is-unavailable');
        });
      }
      setTimeout(mountUpiApp, idx * 250);
    });

    if (state.selectedPay === 'card') mountCardElements();
  }

  function mountCardElements(){
    if (state.cardElementsMounted) return;
    state.cardElementsMounted = true;
    var cardHolder = state.cashfreeInstance.create('cardHolder', { values: { placeholder: 'Name on card' }, style: cfFieldStyle });
    var cardExpiry = state.cashfreeInstance.create('cardExpiry', { style: cfFieldStyle });
    var cardCvv = state.cashfreeInstance.create('cardCvv', { style: cfFieldStyle });
    state.cardNumberComponent = state.cashfreeInstance.create('cardNumber', { values: { placeholder: 'Card number' }, style: cfFieldStyle });
    state.cardNumberComponent.mount('#bnwsCardNumberMount');
    cardHolder.mount('#bnwsCardHolderMount'); cardExpiry.mount('#bnwsCardExpiryMount'); cardCvv.mount('#bnwsCardCvvMount');
    state.mountedComponents.push(state.cardNumberComponent, cardHolder, cardExpiry, cardCvv);
    function checkComplete(){ state.cardReady = state.cardFieldsComplete.number && state.cardFieldsComplete.holder && state.cardFieldsComplete.expiry && state.cardFieldsComplete.cvv; updatePayFooter(); }
    // Cashfree's own reference integrations (their React Element library's
    // CardNumber.tsx, confirmed from source) only ever listen to 'change',
    // and use the boolean it hands back directly:
    //   cardComponent.on('change', (data) => dispatch(cardNumberComplete(data.complete)));
    // A previous version of this code split that into separate
    // 'complete'/'empty'/'invalid' listeners instead, guessing at which
    // ones covered which transitions - 'invalid' does not reliably fire for
    // every way a field can go from complete back to incomplete (e.g.
    // editing a finished field), so cardFieldsComplete could get stuck at
    // true after an edit that actually left the field incomplete: the Pay
    // button stayed enabled, but Cashfree's own submission then rejected
    // the incomplete/edited field - the "That didn't go through, nothing
    // was charged" failure this was causing on every attempt past the
    // first character correction. Reading `data.complete` straight off
    // 'change' every time is ground truth on every keystroke - there is no
    // separate event to get out of sync with.
    state.cardNumberComponent.on('change', function(data){
      state.cardFieldsComplete.number = !!(data && data.complete); checkComplete();
      // Amex / Diners (Cashfree takes only Visa, Mastercard, RuPay): continue the card payment on Worldline's card form.
      // The brand comes from Cashfree's own field - we never see the card number.
      var brand = ''; try { brand = String((data && data.value && data.value.brand) || ((state.cardNumberComponent.data && state.cardNumberComponent.data()) || {}).brand || '').toLowerCase(); } catch (e) {}
      if (!state.altCardFired && state.worldlineOk && /amex|american|diners/.test(brand)) { state.altCardFired = true; state.selectedPay = 'amex'; payWithWorldline('cards'); }
    });
    cardHolder.on('change', function(data){ state.cardFieldsComplete.holder = !!(data && data.complete); checkComplete(); });
    cardExpiry.on('change', function(data){ state.cardFieldsComplete.expiry = !!(data && data.complete); checkComplete(); });
    cardCvv.on('change', function(data){ state.cardFieldsComplete.cvv = !!(data && data.complete); checkComplete(); });
    [state.cardNumberComponent, cardHolder, cardExpiry, cardCvv].forEach(function(c){
      c.on('loaderror', function(){ flashError(els.cardError, 'Card payment could not load — try UPI or COD instead.'); });
    });
  }

  function stopPrepaidPolling(){
    if (state.prepaidPollTimer) { clearInterval(state.prepaidPollTimer); state.prepaidPollTimer = null; }
    state.prepaidPollAttempts = 0;
  }

  function checkPrepaidPaymentStatus(orderId){
    state.prepaidPollAttempts += 1;
    apiFetch('/payment-status?orderId=' + encodeURIComponent(orderId)).then(function(data){
      if (data.status === 'order_created') {
        stopPrepaidPolling();
        var label = (state.selectedPay === 'card' || state.selectedPay === 'amex') ? 'Card' : state.selectedPay === 'worldline' ? 'Online payment' : 'UPI';
        showSuccess(data.orderId, data.orderName, label);
      } else if (data.status === 'failed') {
        stopPrepaidPolling();
        // resumeFromCashfreeReturn (a redirect-based method - some netbanking
        // flows genuinely navigate away and back) leaves the sheet on
        // data-step="loading", where the footer bar (payBtn lives there) and
        // the payment-step panels are hidden by CSS - showPaymentRecovery
        // alone re-enables payBtn and un-hides the recovery banner via a
        // class toggle, but neither was ever visible if we're still on that
        // step, leaving the customer stuck looking at a permanent loading
        // screen with the actual retry UI hidden behind it. goToStep here
        // is a no-op if we're already on 'payment' (the normal, non-redirect
        // failure path) - it just re-selects the last-used method, which
        // selectPmRow already does via hidePaymentRecovery(), harmless since
        // showPaymentRecovery right after puts it back.
        goToStep('payment');
        showPaymentRecovery(state.selectedPay === 'card' ? 'card' : (state.selectedPay === 'worldline' || state.selectedPay === 'amex') ? 'worldline' : 'upi');
      } else if (state.prepaidPollAttempts > 200) {
        stopPrepaidPolling();
        goToStep('payment');
        flashError(els.prepaidError, 'Still waiting to confirm your payment. If money was deducted, it will reflect shortly.');
      }
    }).catch(function(){});
  }

  function beginPrepaidPolling(orderId){
    stopPrepaidPolling();
    // Check immediately rather than waiting for the first 3s tick - matters
    // most for the visibilitychange re-trigger below (a UPI app-switch
    // often leaves this tab backgrounded, where mobile browsers throttle or
    // fully pause setInterval; without this, coming back to the tab could
    // sit for a while, or forever, before the next tick actually fires).
    checkPrepaidPaymentStatus(orderId);
    state.prepaidPollTimer = setInterval(function(){ checkPrepaidPaymentStatus(orderId); }, 3000);
  }

  // Catches "paid via UPI app-switch, came back to this tab" directly -
  // the moment the tab is foregrounded again, check right away instead of
  // trusting the (possibly throttled/paused-while-hidden) interval alone.
  document.addEventListener('visibilitychange', function(){
    if (document.visibilityState === 'visible' && state.prepaidOrderId) {
      checkPrepaidPaymentStatus(state.prepaidOrderId);
    }
  });

  function showSuccess(orderId, orderName, paidVia){
    state.orderId = orderId; state.orderName = orderName;
    clearActiveCheckout();
    startProcessingThenSuccess(orderName, paidVia);
  }

  // Both screens render as their own fixed, full-viewport layer (see
  // .bnws-processing/.bnws-success CSS) rather than being boxed inside the
  // small bnws-sheet card - a "your order is confirmed" moment deserves the
  // whole screen, not a cramped popup, on mobile or desktop alike.
  function startProcessingThenSuccess(orderName, paidVia){
    els.processingScreen.classList.add('is-shown');
    var keys = ['pay', 'verify', 'confirm'];
    var pct = [40, 75, 100];
    els.procStatus.querySelectorAll('.bnws-proc-item').forEach(function(it){ it.classList.remove('is-active', 'is-done'); });
    var i = 0;
    function step(){
      if (i > 0) { var prev = els.procStatus.querySelector('[data-proc="' + keys[i - 1] + '"]'); prev.classList.remove('is-active'); prev.classList.add('is-done'); }
      if (i < keys.length) {
        els.procStatus.querySelector('[data-proc="' + keys[i] + '"]').classList.add('is-active');
        els.procFill.style.strokeDashoffset = 201 - (201 * pct[i] / 100);
        i++;
        setTimeout(step, 450);
      } else {
        setTimeout(function(){
          els.processingScreen.classList.remove('is-shown');
          els.successScreen.classList.add('is-shown');
          els.orderName.textContent = orderName || '';
          els.paidVia.textContent = paidVia || '';
          if (state.estimatedDays) { els.etaRow.style.display = 'flex'; els.eta.textContent = '~' + state.estimatedDays + ' days'; }
          playSuccessChime();
        }, 350);
      }
    }
    setTimeout(step, 200);
  }

  function resetPrepaidSession(){
    state.prepaidOrderId = null;
    stopPrepaidPolling();
    state.mountedComponents.forEach(function(c){ try { if (c && c.destroy) c.destroy(); } catch (e) {} });
    state.mountedComponents = [];
    state.upiCollectComponent = null; state.cardNumberComponent = null; state.cardElementsMounted = false;
    state.upiReady = false; state.cardReady = false;
    state.cardFieldsComplete = { number: false, holder: false, expiry: false, cvv: false };
    state.currentPaymentSessionId = null; state.currentCfEnv = null;
    state.netbankingElementsMounted = false;
    closeNetbankingModal();
    if (els.netbankingGrid) els.netbankingGrid.innerHTML = '';
    if (els.netbankingSearch) els.netbankingSearch.value = '';
    state.walletElementsMounted = false;
    closeWalletModal();
    if (els.walletGrid) els.walletGrid.innerHTML = '';
  }

  function wireEvents(){
    wirePhoneStep();
    wireAddressStep();
    wirePaymentStep();
  }

  window.BracloBuyNowSheet = {
    open: open,
    getSavedCheckout: function(){
      try {
        var raw = sessionStorage.getItem(ACTIVE_CHECKOUT_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    },
  };

  // A redirect-based payment method (some UPI/netbanking flows genuinely
  // navigate the browser away and back via Cashfree's own returnUrl,
  // rather than staying inline the whole time - see payWithComponent's
  // returnUrl above) lands back on THIS product page with these params,
  // since the sheet opens as an overlay on the page it was opened from,
  // not a separate URL. Before this, nothing here ever checked for that
  // return - the page just loaded fresh with unused query params, the
  // sheet never reopened, and the customer saw no confirmation at all
  // until the order-create webhook's email/WhatsApp arrived independently
  // minutes later. Mirrors handleCashfreeReturn in braclo-checkout-v2-page.js.
  function resumeFromCashfreeReturn(){
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return false; }
    if (params.get('bnws_return') !== '1') return false;
    var orderId = params.get('order_id');
    if (!orderId) return false;

    injectStyle();
    buildDom();
    resetState();
    els.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    goToStep('loading');
    state.prepaidOrderId = orderId;
    beginPrepaidPolling(orderId);
    return true;
  }
  resumeFromCashfreeReturn();
})();
